import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { scene, camera } from './scene.js';
import {
  onMessage, me, OP, sendWhois, unpackPos, unpackAngle, unpackLean, unpackPitch8,
  unpackEye8, F_ALIVE, F_NPC, F_BURNING, F_CT,
  HELD_ITEM_MASK, HELD_NADE, HELD_ADS, HELD_RELOAD, HELD_DRAW, HELD_SPRINT,
} from './net.js';
import { S, smokes, clock, LEAN_OFFSET, STAND, CROUCH, PRONE } from './state.js';
import { makeNade } from './nademodel.js';
// Человеческая модель поверх того же рига. ⚠️ Импорт ОДНОСТОРОННИЙ: `skinbody.js`
// не знает про `remote.js` вовсе (иначе цикл модулей и TDZ, см. md/models.md).
import {
  makeSkin, syncSkin, dropSkin, patchSkin, firstPersonKit, SKIN_RIG,
} from './skinbody.js';
import { rayWalls } from './player.js';
// Звук чужой перезарядки надо уметь ОБОРВАТЬ (её теперь отменяет бег) —
// см. applyHands. Цикла импортов нет: audio.js знает только про state и net.
import { stopReloadNear } from './audio.js';
// Пламя на горящем теле. Живёт в fx.js, а не в grenades.js: импорт оттуда
// замкнул бы цикл модулей (см. комментарий у самой функции).
import { spawnBodyFire } from './fx.js';

// ============================================================
//  Чужие игроки: приём снапшотов и отрисовка с интерполяцией.
//
//  Почему нельзя рисовать «как пришло»: снапшоты идут 30 раз в секунду, а
//  экран рисует 60-144. Без интерполяции чужие дёргались бы шагами. Поэтому
//  рисуем НЕ последнее состояние, а прошлое (now - delay) — между двумя уже
//  полученными снапшотами. Мы всегда видим чужих чуть в прошлом; это плата
//  за плавность, и её же компенсирует lag compensation на шаге 5.
// ============================================================

const TICK_MS = 1000 / 30;

// Границы буфера интерполяции. Нижняя — чуть больше тика (иначе нечего
// интерполировать), верхняя — предел, после которого задержка уже вреднее
// рывков.
const DELAY_MIN = 60;
const DELAY_MAX = 400;

// Сколько снапшотов держим. 30 Гц × 2 с — с запасом на любой буфер.
const HISTORY = 60;

// Насколько дольше последнего снапшота можно тянуть движение вперёд, когда
// буфер опустел. Размен: слишком много — чужие «проезжают» повороты и потом
// поправляются рывком; слишком мало — возвращается мигание. 100 мс ≈ 3 тика.
const EXTRAPOLATE_MS = 100;

/** Пришедшие снапшоты: {t: время приёма, players: Map<id, state>} */
const snapshots = [];

/** Кто хочет знать о смерти чужого (сейчас — corpse.js).
 *
 *  ⚠️ Подписка, а не прямой импорт `spawnCorpse`, и это не украшательство:
 *  `corpse.js` сам импортирует отсюда сборку тела, и встречный импорт замкнул
 *  бы цикл. Проект такие циклы разрывает намеренно (см. правила структуры
 *  в CLAUDE.md), а приём ровно тот же, что у `onMessage` в `net.js`. */
let deathHook = null;
export function onRemoteDeath(fn) { deathHook = fn; }

/** Меши чужих игроков: id → {group, mesh, label, nick} */
const remotes = new Map();

/** Ники, пришедшие в JOIN (до первого снапшота меша ещё нет) */
const nicks = new Map();

/** Когда игрок последний раз присутствовал в пришедшем снапшоте */
const lastPresent = new Map();

// ---- Добор потерянных ников (OP_WHOIS) ----
//
// JOIN сервер шлёт ОДИН раз. На unreliable-канале WebRTC
// (ordered:false, maxRetransmits:0) этот кадр может потеряться — и тогда
// игрок/бот навсегда остался бы «#id» (у ботов id ≥ 0x8000, то есть длинные
// числа вместо «BOT-1»). Поэтому: встретив в снапшоте id, для которого ника
// нет, клиент сам просит сервер прислать его (тот отвечает обычным JOIN).
// Самовосстановление: потерялся запрос или ответ — следующий снапшот снова
// покажет неизвестный id, и мы спросим опять. Работает одинаково на WS и RTC.
const askedWhois = new Map();          // id → performance.now() последнего запроса
const WHOIS_RETRY = 1000;              // один id спрашиваем не чаще раза в секунду

function askNickIfUnknown(id, now) {
  if (id === me.id || nicks.has(id)) return;
  const last = askedWhois.get(id) || 0;
  if (now - last < WHOIS_RETRY) return;
  askedWhois.set(id, now);
  sendWhois(id);
}

/** Через сколько без единого упоминания в снапшотах выбрасываем меш.
 *  Заметно больше VIS_GRACE сервера (500 мс) — чтобы прячущийся за углом
 *  игрок не пересоздавался при каждом выглядывании. */
const FORGET_MS = 4000;

// ---- Оценка джиттера ----
//
// Замер владельца: пинг 192-307 мс, то есть разброс ±60 мс. Фиксированный
// буфер 100 мс при таком джиттере регулярно оказывается пустым — следующий
// снапшот ещё не пришёл, интерполировать не из чего, и чужих телепортирует.
// Поэтому буфер адаптивный: измеряем СКО интервалов прихода и держим запас
// в 2σ. Так делает Valorant; фиксированная константа — рецепт из туториалов,
// которые пишут на localhost, где джиттера нет вовсе.
let lastRecv = 0;
let jitterMean = TICK_MS;
let jitterVar = 0;
let interpDelay = 100;
/** Время приёма снапшота, на котором уже отработали расширение буфера
 *  (см. `updateRemotes`): помечает эпизод голодания, чтобы не растить
 *  задержку повторно каждым кадром отрисовки. */
let starveMark = 0;

function trackJitter(now) {
  if (lastRecv) {
    const gap = now - lastRecv;
    // экспоненциальные среднее и дисперсия — дёшево и без хранения истории
    const d = gap - jitterMean;
    jitterMean += d * 0.1;
    jitterVar += (d * d - jitterVar) * 0.1;
    const sigma = Math.sqrt(Math.max(jitterVar, 0));
    const target = Math.max(DELAY_MIN, Math.min(DELAY_MAX, TICK_MS + 2 * sigma));
    // К большей задержке переходим быстро (иначе картинка рвётся прямо
    // сейчас), к меньшей — медленно (иначе буфер «дышит» на каждом всплеске).
    interpDelay += (target - interpDelay) * (target > interpDelay ? 0.3 : 0.02);
    S.interpDelay = interpDelay;
    S.netJitter = sigma;
  }
  lastRecv = now;
}

// ---- Приём кадров ----

onMessage((op, d, buf) => {
  if (op === OP.JOIN) {
    const id = d.getUint16(1, true);
    const len = d.getUint8(3);
    nicks.set(id, new TextDecoder().decode(new Uint8Array(buf, 4, len)));
    askedWhois.delete(id);            // ник приехал — запрос больше не нужен
    // Меш мог появиться раньше кадра JOIN (переподключение, потерянный порядок) —
    // тогда на табличке стоит запасное «#id». Перерисовываем, иначе ник
    // не появится уже никогда: label делается один раз при создании меша.
    retitle(id);
    return;
  }
  if (op === OP.LEAVE) {
    const id = d.getUint16(1, true);
    removeRemote(id);
    askedWhois.delete(id);
    // Ник забываем ТОЛЬКО по настоящему LEAVE (игрок вышел из комнаты), а не
    // при выгрузке меша по FORGET_MS. JOIN сервер шлёт лишь однажды, поэтому
    // удаление ника вместе с мешом (уход за угол по PVS) приводило к тому, что
    // вернувшийся игрок/бот показывался как «#id» вместо имени — а у NPC
    // (id ≥ 0x8000) это длинные числа вместо «BOT-1». Жалоба владельца.
    nicks.delete(id);
    return;
  }
  if (op !== OP.SNAPSHOT) return;

  const now = performance.now();
  trackJitter(now);

  const count = d.getUint8(9);
  const players = new Map();
  let o = 10;
  for (let i = 0; i < count; i++) {
    const id = d.getUint16(o, true);
    // Нет ника для этого id (кадр JOIN потерялся) — просим прислать. Здесь,
    // а не в отрисовке: id виден в снапшоте раньше, чем создаётся его меш.
    askNickIfUnknown(id, now);
    players.set(id, {
      x: unpackPos(d.getInt16(o + 2, true)),
      y: unpackPos(d.getInt16(o + 4, true)),
      z: unpackPos(d.getInt16(o + 6, true)),
      yaw: unpackAngle(d.getUint16(o + 8, true)),
      flags: d.getUint8(o + 10),
      hp: d.getUint8(o + 11),
      npc: (d.getUint8(o + 10) & F_NPC) !== 0,
      // Наклон приходит УЖЕ обрезанным упором в стену и нормированным на
      // LEAN_OFFSET: тело обязано рисоваться ровно там, где сервер считает
      // хитбоксы, иначе картинка и попадания разъедутся.
      lean: unpackLean(d.getInt8(o + 12)) * LEAN_OFFSET,
      // Питч — только для поворота рук: на попадания он не влияет ни здесь,
      // ни на сервере (тело там — вертикальная стопка коробок).
      pitch: unpackPitch8(d.getInt8(o + 13)),
      // Что в руках: оружие/граната + прицеливание, перезарядка, доставание.
      // Разбирается в `applyHands`; на попадания не влияет (см. net.js).
      held: d.getUint8(o + 14),
      // Высота глаз — это ПОЗА, и в отличие от питча она не косметика:
      // по ней раскладывается тело (`layoutBody`), и по ней же сервер считает
      // коробки попаданий. Флаги F_CROUCH/F_PRONE для этого не годятся —
      // они везут намерение, а тело опускается плавно.
      eye: unpackEye8(d.getUint8(o + 15)),
    });
    lastPresent.set(id, now);
    o += 16;
  }
  snapshots.push({ t: now, players });
  if (snapshots.length > HISTORY) snapshots.shift();
});

// ---- Меши ----

function nickTexture(text) {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 64;
  const g = c.getContext('2d');
  g.font = 'bold 30px system-ui, sans-serif';
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.lineWidth = 6; g.strokeStyle = 'rgba(0,0,0,.85)';
  g.strokeText(text, 128, 32);
  g.fillStyle = '#e5e7eb';
  g.fillText(text, 128, 32);
  return new THREE.CanvasTexture(c);
}

function makeNickLabel(text) {
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({
    map: nickTexture(text), depthTest: false, transparent: true,
  }));
  sp.scale.set(2.0, 0.5, 1);
  return sp;
}

/** Перерисовать табличку уже существующего игрока (ник приехал позже меша). */
function retitle(id) {
  const r = remotes.get(id);
  if (!r) return;
  r.label.material.map.dispose();       // старая текстура иначе течёт в GPU
  r.label.material.map = nickTexture(nicks.get(id) || `#${id}`);
  r.label.material.needsUpdate = true;
}

// ---- Фигура игрока (R15, стиль Roblox) ----
//
// Тело собрано из 15 коробок: голова, две половины торса, по три сегмента на
// руку и на ногу. Скелета и скиннинга здесь нет — это коробки, скреплённые
// шарнирами, и анимация сводится к повороту шарнира. Стиль выбран не ради
// вида, а ради двух следствий: серверу нечего дублировать (хитбоксы остались
// коробками, см. ниже), а скины позже кладутся материалом на каждую часть —
// без UV-развёртки и привязки к костям.
//
// ⚠️ Голова, верх торса и таз — по-прежнему ЗЕРКАЛО `shared/src/hit.rs`,
// менять только парой. Это ровно те коробки, по которым сервер считает
// попадания: игрок целится в то, что нарисовано, а засчитывает сервер.
//
// ⚠️ Руки и ноги — ЧИСТАЯ ГРАФИКА, сервер о них не знает. Ноги делят регион
// ног пополам и в сумме занимают ту же коробку, поэтому силуэт снизу не
// изменился. А руки висят СНАРУЖИ хитбокса торса — попадание в вытянутую руку
// не засчитается. Это осознанный размен (так же живёт CS): альтернатива —
// анимировать конечности на сервере, то есть продублировать анимационный граф
// в Rust и в JS побитово согласованно. Тот же капкан, что с физикой
// (`movement.rs` ↔ `player.js`), только заметно хуже.
const HEAD_TOP = 0.12;      // макушка выше глаз
const HEAD_BOTTOM = 0.10;   // подбородок ниже глаз
const TORSO_BOTTOM = 0.55;
const PELVIS_BOTTOM = 0.80;
const HEAD_HALF = 0.12;
const TORSO_HALF_X = 0.26, TORSO_HALF_Z = 0.18;
const PELVIS_HALF_X = 0.21, PELVIS_HALF_Z = 0.17;
// Полуглубины коробки ног (`LEGS_HALF_Z` в hit.rs) здесь нет: ноги рисуются
// двумя цепочками сегментов со своей, заметно меньшей толщиной — см. LEG_HALFS.
const LEGS_HALF_X = 0.20;

// ---- Позы: присед и лёжа меняют РАСКЛАДКУ, а не только высоту ----
//
// ⚠️ Всё, что ниже, — зеркало `shared/src/hit.rs` (`KNEEL_*` / `PRONE_*` и
// функции `kneel_amount`/`prone_amount`), менять только парой. Раньше расходиться
// было почти нечему: обе стороны просто опускали уровень глаз, и «сжатый
// столбик» на экране совпадал со «сжатым столбиком» в хитбоксах. Теперь по этим
// числам сервер решает, куда попала пуля, а мы — где нарисовать колено; разъедутся
// они, и вернётся ровно та беда, ради которой поза сюда и приехала.
// ⚠️ Коробки ног (`KNEEL_LEGS_*` и `PRONE_LEGS_*` в hit.rs) здесь ОТСУТСТВУЮТ
// намеренно: ноги у нас не коробка, а цепочка сегментов на шарнирах. Зеркалить
// нужно не число, а результат — сложенная нога обязана уложиться в серверную
// коробку. Держат это углы поз ниже (`SQUAT_*`, `KNEE_DOWN_*`, `PRONE_HIP_A`)
// вместе с точками крепления: тронул их — пересчитай, куда уехали колено
// и ступня, и подвинь коробку в hit.rs.
const PRONE_HEAD_Y0 = 0.22, PRONE_HEAD_Y1 = 0.58, PRONE_HEAD_HALF_Z = 0.15;
const PRONE_TORSO_CZ = 0.42, PRONE_TORSO_HALF_Z = 0.30, PRONE_TORSO_Y1 = 0.38;
const PRONE_PELVIS_CZ = 0.82, PRONE_PELVIS_HALF_Z = 0.16, PRONE_PELVIS_Y1 = 0.32;

/** Насколько игрок присел: 0 — стоит, 1 — присед и ниже. Зеркало hit.rs. */
const kneelAmount = (eye) => Math.max(0, Math.min(1, (STAND - eye) / (STAND - CROUCH)));
/** Насколько игрок лёг: 0 — присед и выше, 1 — лежит. Зеркало hit.rs. */
const proneAmount = (eye) => Math.max(0, Math.min(1, (CROUCH - eye) / (CROUCH - PRONE)));

const mix = (a, b, k) => a + (b - a) * k;

// Куда переезжают точки крепления конечностей, когда игрок лёг. Тело лежит
// вдоль локальной +Z (взгляд — это −Z), поэтому таз и плечи уходят «за
// голову». Числа согласованы с коробками PRONE_* выше: бедро начинается там,
// где кончается таз, плечо — на верху лежащего торса.
const PRONE_HIP_Y = 0.13, PRONE_HIP_CZ = 0.80;
const PRONE_SHOULDER_Y = 0.34, PRONE_SHOULDER_CZ = 0.34;

// ⚠️ Цели ступней и углы поз объявлены НИЖЕ, сразу за `LEG_SEGS`, а не здесь,
// рядом с остальными константами позы. Причина не в красоте: `ANKLE_Y`
// выводится из длины ступни, и стоя он выше `LEG_SEGS`, модуль падает при
// загрузке с «Cannot access before initialization» — см. баг-урок в 2.3.4.

// --- Конечности: раскладка поверх хитбокс-регионов ---
const SHOULDER_DROP = 0.16;             // плечо ниже уровня глаз
// ⚠️ Длины руки подняты вместе с приходом хвата (было [0.26, 0.24, 0.12]),
// и это не косметика. Кисть теперь обязана ДОТЯГИВАТЬСЯ до цевья, вынесенного
// почти на полметра вперёд от плеча (см. `reachArm`): на прежней длине левая
// рука до него не доставала и вытягивалась в струну. Заодно 0.67 от плеча до
// кончиков пальцев анатомичнее прежних 0.62 для человека ростом 1.85.
const ARM_SEGS = [0.28, 0.26, 0.13];    // длины: плечо / предплечье / кисть
const ARM_FULL = ARM_SEGS[0] + ARM_SEGS[1] + ARM_SEGS[2];

// ⚠️ Насколько рука утоплена в торс — и это не косметика. Хитбокс торса
// намеренно щедрый (полуширина 0.26, то есть 0.52 в плечах — уже примерно
// весь человек с руками). Повесь руки СНАРУЖИ с зазором — получишь размах
// 0.84 м, культуриста вместо человека. А главное, чем сильнее рука перекрыта
// торсом, тем меньше та самая зона, где выстрел в видимую руку не
// засчитывается: перекрытие прямо уменьшает расхождение картинки с уроном.
//
// ⚠️ Поднято с 0.05 до 0.11 вместе с приходом хвата оружия. При 0.05 плечи
// разъезжались на 0.57 м — шире реальных 0.40, — и левая рука физически не
// доставала до цевья: точка хвата уезжала дальше длины руки. Утопление
// работает сразу в обе стороны: силуэт сужается до человеческого, а зона
// «попал в видимую руку, а не засчиталось» уменьшается.
const ARM_INSET = 0.11;
const ARM_HALF_X = 0.075;
const LEG_HALF_X = 0.095;
const LEG_FRACS = [0.50, 0.40, 0.10];   // бедро / голень / ступня — доли региона ног

// ⚠️ Длины ног АБСОЛЮТНЫ, как у рук, и это не мелочь — на этом держится вся
// естественность приседа. Раньше сегменты масштабировались по высоте таза
// (`hipY * LEG_FRACS`), то есть присевший игрок не складывал ноги, а ВТЯГИВАЛ
// их: на уровне глаз 1.0 таз оказывается на высоте 0.20, и нога честно
// съёживалась до 20 см. Именно это читалось как «персонажа свернули». Теперь
// длина постоянна, а поза набирается поворотом шарниров (см. `poseLegs`).
//
// Числа выводятся из региона ног стоя, а не задаются: `LEG_SEGS` в сумме
// обязаны давать высоту таза стоя, иначе стоящий игрок начнёт проваливаться
// под пол или висеть над ним. Формулой это не разъедется никогда.
const LEG_SEGS = LEG_FRACS.map((f) => f * (STAND - PELVIS_BOTTOM));

// ---- Присед: куда игрок ставит ступни ----
//
// ⚠️ Углы шарниров здесь НЕ заданы числами, и это принципиально. Заданы
// ЦЕЛИ — куда встаёт ступня, — а углы бедра и колена считает `solveLeg`.
// Причина: высота таза едет непрерывно (глаза опускаются плавно), и любые
// «правильные для полного приседа» углы на середине перехода вгоняют колено
// под пол или подвешивают ступню в воздухе. Цель же верна на всей дороге:
// ступня стоит на полу и стоя, и на полпути, и внизу.
//
// Ступни разнесены вперёд-назад: две симметрично сложенные ноги читаются как
// «сел на корточки на обе», а выставленная вперёд опорная — как боевой присед.
//
// ⚠️ Блок стоит ИМЕННО ЗДЕСЬ, а не выше с остальными константами позы:
// `ANKLE_Y` читает `LEG_SEGS`, и объявление выше него роняет весь модуль
// при загрузке (баг-урок в 2.3.4).
const FRONT_FOOT_Z = -0.28;    // левая ступня — вынесена вперёд
const BACK_FOOT_Z = -0.14;     // правая — почти под тазом
/** Высота лодыжки: ровно длина ступни, чтобы подошва легла на пол.
 *  ⚠️ Не «примерно ноль» — тот же урок, что у `LIMB_LIFT` в corpse.js:
 *  «положить на поверхность» считается по нижней грани, а не по оси. */
const ANKLE_Y = LEG_SEGS[2];

// ---- Утиный шаг: присед НА ХОДУ ----
//
// ⚠️ Присев, игрок продолжает ходить (3.2 м/с, `movement.rs::SPEED_CROUCH`),
// и до этого шага ноги при этом стояли КАМНЕМ: поза приседа кладётся весом
// `kneel`, а на полном приседе вес равен 1 — то есть качание из `animateLimbs`
// перезаписывалось целиком (жалоба владельца: «на корточках ноги неподвижны»).
// Гасить вес позы нельзя (нога тут же разогнётся и вылезет из-под пола), а
// вот ЦЕЛИ ступней двигать можно: их отрабатывает та же ИК, что и в покое,
// поэтому ступня остаётся на полу на любой фазе шага.
//
// Стоя на месте ступни разнесены вперёд-назад (боевой присед). На ходу этот
// разнос сходится к общей середине: шагающий человек не держит ноги врозь,
// он переставляет их по очереди.
//
// ⚠️ Середина взята НЕ как среднее между `FRONT_FOOT_Z` и `BACK_FOOT_Z`
// (−0.21), а заметно дальше вперёд, и это посчитано, а не подобрано: чем
// ближе ступня к тазу, тем сильнее колено уходит вниз, и на −0.05 оно
// пробивает пол на 2 см (при среднем −0.21 и размахе 0.16 нога уходила под
// землю на 13.5 см). На −0.24 ± размах самая дальняя назад ступня остаётся
// на −0.12, и колено держится выше пола на всей фазе.
const KNEEL_MID_Z = -0.24;
// Размах и подъём ступни. ⚠️ Оба упираются не в анатомию, а в серверную
// коробку ног присевшего (`hit.rs`: по локальной Z от −0.60 до +0.12, верх
// на 0.60 над полом): нога обязана остаться внутри, иначе по видимому колену
// и носку перестанет засчитываться попадание. При этих числах самый дальний
// носок садится ровно на −0.600, а верх колена — на 0.596.
const KNEEL_STRIDE = 0.12;
const KNEEL_LIFT = 0.07;
/** Скорость, на которой утиный шаг набирает полный размах. Примерно скорость
 *  приседа, но ТОЧНОЕ совпадение с `SPEED_CROUCH` не нужно и не проверяется:
 *  это амплитуда анимации, а не физика. Заводить здесь ещё одну копию числа
 *  из `movement.rs` — плодить зеркало, которое некому держать. */
const KNEEL_FULL_SPEED = 3.0;

// Лёжа: нога вытянута назад вдоль тела (−π/2 разворачивает цепочку в +Z).
const PRONE_HIP_A = -Math.PI / 2;
/** Базовое разведение ног лежащего: две строго параллельные коробки читаются
 *  как хвост, а не как ноги. */
const PRONE_SPREAD = 0.09;

// ---- Ползание: гребок ногами по очереди ----
//
// ⚠️ Лёжа тоже можно двигаться (1.6 м/с, `movement.rs::SPEED_PRONE`), и до
// этого шага лежащий ЕХАЛ, не шевелясь вовсе, — по той же причине, что и
// присевший (вес позы 1 перезаписывал качание). Ползут ногами: бедро
// отводится вбок, колено сгибается в ту же сторону, голень чуть приподнимается
// над землёй — и всё это по очереди, левая нога против правой.
//
// ⚠️ Шарнир колена лёжа гнёт голень В СТОРОНУ, а не вверх, и это не опечатка:
// бедро повёрнуто на −π/2 вокруг X, значит локальная ось Z колена смотрит
// в мировой «верх», и поворот вокруг неё разводит голень горизонтально. Вверх
// голень поднимает `rotation.x` — им и сделан подъём.
//
// ⚠️ ОСОЗНАННЫЙ РАЗМЕН, посчитанный, а не «наверное, сойдёт»: на гребке нога
// выходит за серверную коробку ног лежащего вбок. Коробка узка сама по себе
// (`hit.rs`: half_x 0.20 не меняется от позы), и статичное разведение уже даёт
// 0.285; гребок доводит до 0.450. То есть выстрел точно в отведённое колено
// в верхней точке гребка может не засчитаться — ровно та же цена, что у
// вытянутой руки (см. 2.3.2), и по той же причине: коробки на сервере
// анимации не знают.
// Хочется убрать — расширять `PRONE_LEGS_HALF_X` в `hit.rs`, но это правка
// ПАРЫ и решение по балансу: лежащий станет заметно шире для пуль.
//
// ⚠️ Вверх голень поднимается ровно настолько, чтобы остаться в коробке по
// высоте (её верх — 0.28 над полом): при этих числах самая высокая точка ноги
// 0.272. Вот здесь запаса нет вовсе, и подъём крутить нельзя.
const CRAWL_HIP_OUT = 0.08;    // насколько бедро отводится вбок сверх базы
const CRAWL_KNEE_OUT = 0.20;   // насколько голень уходит вбок за коленом
const CRAWL_KNEE_UP = 0.12;    // насколько голень приподнимается над землёй
/** Скорость полного гребка — примерно скорость ползания (`SPEED_PRONE`).
 *  Точность не нужна по той же причине, что у `KNEEL_FULL_SPEED`. */
const CRAWL_FULL_SPEED = 1.5;
// Лёжа: плечо почти вертикально вниз (локоть в землю), предплечье вперёд —
// то есть человек лежит на локтях, а не роняет руки вдоль тела.
const PRONE_ARM = 0.15, PRONE_ELBOW = 1.42;

// ⚠️ Центр ноги считается ОТ хитбокса, а не задан числом: `LEG_SPAN +
// LEG_HALF_X` обязано равняться `LEGS_HALF_X`, иначе две ноги перестанут
// занимать ровно ту ширину, что раньше занимала одна коробка, и силуэт
// разойдётся с зоной попаданий. Формулой это не разъедется никогда.
const LEG_SPAN = LEGS_HALF_X - LEG_HALF_X;

// Полуразмеры сегментов: [поперёк, в глубину, сдвиг вперёд]. Голень тоньше
// бедра, ступня длиннее и вынесена вперёд (−Z) — иначе нога кончается обрубком.
const ARM_HALFS = [
  [ARM_HALF_X, 0.085, 0],
  [ARM_HALF_X * 0.93, 0.080, 0],
  [ARM_HALF_X, 0.090, 0],
];
// ⚠️ Глубина бедра и голени (0.115/0.098) заметно МЕНЬШЕ полуглубины коробки
// ног в хитбоксе (0.16), и это не рассинхрон. Коробка покрывает ОБЕ ноги
// вместе с их разбросом вперёд-назад, а одна нога такой толстой быть не
// должна — при ширине 0.19 глубина 0.32 давала «ласты». Заметно это стало
// с появлением позы «лёжа»: там глубина ноги становится её ВЫСОТОЙ, и
// лежащий получал ноги толще собственного торса.
const LEG_HALFS = [
  [LEG_HALF_X, 0.115, 0],
  [LEG_HALF_X * 0.90, 0.098, 0],
  [LEG_HALF_X, 0.19, -0.05],
];

// Геометрия общая на всех: коробки отличаются только масштабом, а плодить их
// на каждого игрока — лишняя работа для GPU и сборщика мусора.
const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);

// Наборы материалов («скин») — одна точка, куда позже подставятся текстуры
// частей. Конечности чуть темнее корпуса: 15 одноцветных коробок сливаются
// в пятно и читаются хуже прежних четырёх, а силуэт в шутере важнее деталей.
//
// Цвет несёт смысл, а не украшает: красный силуэт означает «живой противник,
// он может выстрелить в ответ», серый — мишень, и путать их нельзя. Голова
// светлее тела у обоих — зона хедшота должна читаться глазом.
const KIT_PLAYER = {
  head: new THREE.MeshStandardMaterial({ color: 0xf87171, roughness: 0.6 }),
  body: new THREE.MeshStandardMaterial({ color: 0xef4444, roughness: 0.7 }),
  limb: new THREE.MeshStandardMaterial({ color: 0xb91c1c, roughness: 0.75 }),
};
// ⚠️ БОТЫ СИНИЕ, А НЕ СЕРЫЕ. Серый выбирался, когда карта была блокаутом из
// одинаковых серо-песочных коробок, — и именно на ней бот в неё и пропадал
// (жалоба владельца «не видно персонажей»). Вся палитра карты тёплая
// (песок, кирпич, дерево, бетон), поэтому холодный синий не встречается на
// фоне НИГДЕ и силуэт читается на любой стене. Смысл цвета сохранён:
// красный — живой противник, не-красный — мишень.
const KIT_NPC = {
  head: new THREE.MeshStandardMaterial({ color: 0x9fc0e8, roughness: 0.7 }),
  body: new THREE.MeshStandardMaterial({ color: 0x4a6f9e, roughness: 0.8 }),
  limb: new THREE.MeshStandardMaterial({ color: 0x33507a, roughness: 0.85 }),
};
// ⚠️ СОЮЗНИК — ЗЕЛЁНЫЙ, и это про матч, а не про красоту (появилось вместе с
// дезматчем). Смысл цвета в шутере один: «можно ли в это стрелять». Красный —
// враг, зелёный — свой, и путать их дороже всего именно в командном режиме,
// где половина карты бегает за тебя. Тёплой зелени на de_dust2 нет нигде, так
// что силуэт читается на любой стене — тот же довод, по которому боты синие.
//
// ⚠️ Набор выбирается по КОМАНДЕ, а не по «бот/игрок»: союзник-бот обязан
// выглядеть союзником. Отличить бота всё равно можно — над ним полоска HP.
const KIT_ALLY = {
  head: new THREE.MeshStandardMaterial({ color: 0xa7f3d0, roughness: 0.6 }),
  body: new THREE.MeshStandardMaterial({ color: 0x34d399, roughness: 0.7 }),
  limb: new THREE.MeshStandardMaterial({ color: 0x15803d, roughness: 0.75 }),
};

// ⚠️ «Голова светлее тела» у КОРОБОК получается сама (у головы свой материал),
// а у человеческой модели тело — один меш с одним материалом. Поэтому на нём
// патч шейдера, красящий вершины головы тем же цветом `kit.head`: цвет остаётся
// в одном месте, и коробочный откат с моделью не разъедется. Патч безвреден
// для самих коробок — у них нет разметки вершин, и они читают «корпус».
patchSkin(KIT_PLAYER.body, { head: KIT_PLAYER.head.color });
patchSkin(KIT_NPC.body, { head: KIT_NPC.head.color });
patchSkin(KIT_ALLY.body, { head: KIT_ALLY.head.color });

/**
 * Какой набор материалов дать этому телу.
 *
 * Порядок проверок важен: СОЮЗНИК важнее того, бот он или человек. В дезматче
 * половина карты играет за тебя, и «в кого стрелять» — первое, что игрок
 * читает по цвету; серо-синий союзник-бот прочитался бы как враг.
 *
 * ⚠️ Функция чисто про картинку. Кому можно нанести урон, решает сервер по
 * своей копии команды (`room.rs::friendly`) — здесь только цвет.
 */
function kitFor(isNpc, ally) {
  if (ally) return KIT_ALLY;
  return isNpc ? KIT_NPC : KIT_PLAYER;
}

/** Размеры рига КОРОБОЧНОГО тела — те же числа, что были константами.
 *
 *  ⚠️ Риг стал полем тела (`r.rig`) только затем, чтобы человеческая модель
 *  могла принести свои пропорции: у неё бедро выше, плечо ниже и рука короче
 *  (см. `skinbody.js::SKIN_RIG`). Здесь всё осталось как было — коробочный
 *  путь обязан работать ровно так же, он и есть откат.
 *
 *  ⚠️ Это НЕ хитбоксы: коробки корпуса по-прежнему считаются от высоты глаз
 *  константами `hit.rs` и от рига не зависят. Риг задаёт только, где растут
 *  конечности и какой они длины. */
const BOX_RIG = {
  hipDrop: PELVIS_BOTTOM,
  shoulderDrop: SHOULDER_DROP,
  armX: TORSO_HALF_X + ARM_HALF_X - ARM_INSET,
  legSpan: LEG_SPAN,
  legSegs: LEG_SEGS,
  armSegs: ARM_SEGS,
  ankleY: ANKLE_Y,
  armFull: ARM_FULL,
};

/** Показывать человеческую модель? Откат к коробкам — `?body=boxes` в адресе.
 *
 *  Через адрес, а не клавишей: переключение на лету означало бы пересобрать
 *  тела всех живых, трупов и своё — в середине боя это заметная пауза и лишний
 *  класс ошибок ради кнопки, которая нужна раз в месяц. Перезагрузить вкладку
 *  дешевле и надёжнее. */
const useSkin = () => !/(\?|&)body=boxes(&|$)/.test(location.search);

function makePart(material) {
  const m = new THREE.Mesh(UNIT_BOX, material);
  m.castShadow = true;
  return m;
}

/** Материалы примитива-фолбэка ствола — общие, как и материалы тела.
 *  Материалы гранат сюда не входят: их даёт GLB-шаблон в makeNade (nademodel.js),
 *  а трупу личные клоны возвращает сама makeNade через `own`. */
const GUN_MATS = () => ({ body: GUN_BODY, barrel: GUN_BARREL });

/** Личная копия всех материалов тела и оружия.
 *
 *  Нужна ровно одному потребителю — трупу (`corpse.js`), который тает
 *  прозрачностью. Красить общий материал нельзя: он один на всех, и вместе
 *  с трупом растворились бы все живые. Клон делается один раз на смерть,
 *  живёт 5 секунд и освобождается — на кадр это не влияет никак. */
function cloneMaterials(kit) {
  const own = (m) => {
    const c = m.clone();
    c.transparent = true;                // заранее: смена флага на живом
    // ⚠️ `clone()` НЕ копирует патч шейдера: `onBeforeCompile` — поле
    // экземпляра, а `Material.copy` переносит только свой список свойств.
    // Без этих двух строк у трупа молча пропадала бы светлая голова, и
    // ловилось бы это одними глазами.
    c.onBeforeCompile = m.onBeforeCompile;
    c.customProgramCacheKey = m.customProgramCacheKey;
    return c;                            // материале пересобирает шейдер
  };
  return {
    kit: { head: own(kit.head), body: own(kit.body), limb: own(kit.limb) },
    guns: { body: own(GUN_BODY), barrel: own(GUN_BARREL) },
  };
}

/** Сборка тела R15: корпус, конечности и то, что в руках.
 *
 *  Вынесена из `createRemote` затем, что тело нужно ДВУМ потребителям:
 *  живому чужому игроку и трупу. Труп не может забрать группу самого игрока —
 *  тот же id воскресает через 2.5 с, а тело лежит 5 с, и они пересекаются
 *  во времени (осознанное решение, см. CLAUDE.md 2.7).
 *
 *  `own` — делать личную копию материалов (нужно только трупу, см. выше).
 *  `firstPerson` — тело ОТ ПЕРВОГО ЛИЦА: у модели видны только ноги (`body.js`).
 *  Возвращает и список материалов: освобождать и гасить их — забота хозяина. */
export function buildBody(isNpc, own = false, gunGhost = null, wantSkin = true, firstPerson = false,
                          ally = false) {
  const group = new THREE.Group();
  const base = kitFor(isNpc, ally);
  const mine = own ? cloneMaterials(base) : null;
  const kit = mine ? mine.kit : base;
  const gunMats = mine ? mine.guns : GUN_MATS();

  // Корпус: три коробки, повторяющие хитбоксы сервера один в один.
  const parts = {
    head: makePart(kit.head),
    upperTorso: makePart(kit.body),
    lowerTorso: makePart(kit.body),
  };
  for (const p of Object.values(parts)) group.add(p);

  // Конечности: висят на шарнирах, длины и положение ставит `layoutBody`.
  const limbs = {
    armL: makeLimb(group, kit.limb, ARM_HALFS),
    armR: makeLimb(group, kit.limb, ARM_HALFS),
    legL: makeLimb(group, kit.limb, LEG_HALFS),
    legR: makeLimb(group, kit.limb, LEG_HALFS),
  };

  // Что в руках. Висит на группе тела, а НЕ на кисти руки — намеренно: через
  // кисть положение оружия зависело бы от анимации ходьбы, и ствол болтался бы
  // вместе с махом руки. В реальности человек ведёт оружие корпусом, а руки
  // подстраиваются под него. Здесь так же: поза задаёт положение ствола,
  // а `animateLimbs` доводит к нему руки.
  const hands = makeHands(group, gunMats, own, gunGhost);

  // Труп растворяется прозрачностью: в список попадают материалы тела, а также
  // GLB-стволов и гранат (`hands.gunMats`/`hands.nadeMats` — их личные клоны,
  // makeWorldGun/makeNade вернули их при own=true). `mine.guns.body/barrel`
  // держим на случай примитива-фолбэка (если GLB вдруг не готов): гасить лишний
  // неиспользуемый материал безвредно, а забыть используемый — оставить висеть
  // непрозрачный ствол на растаявшем теле.
  const materials = mine
    ? [mine.kit.head, mine.kit.body, mine.kit.limb,
       mine.guns.body, mine.guns.barrel, ...hands.gunMats, ...hands.nadeMats]
    : [];

  // ---- Человеческая модель поверх этого же рига ----
  //
  // Коробки НЕ удаляются: пока GLB летит по сети, тело должно быть видно —
  // невидимый противник это не косметика, а нечестное преимущество. Гасит их
  // сам `makeSkin` в момент подмены.
  //
  // ⚠️ Своё тело от первого лица (`firstPerson`) берёт ТОТ ЖЕ меш, но с обрезанным
  // набором материалов (`firstPersonKit`): видны только ноги, всё остальное
  // режется в шейдере по разметке частей тела. Материалом это сделать нельзя —
  // у модели одно тело и один материал на нём. Тень при этом остаётся целой:
  // теневой проход рисует своим материалом глубины, патча он не видит.
  //
  // ⚠️ А вот труп (`own`) модель ПОЛУЧАЕТ, хотя конечности ему крутит
  // собственная физика (`corpse.js`: «шест из двух точек» и доваливание).
  // Мешать это не может: физика пишет углы в ТЕ ЖЕ шарниры рига, а `syncSkin`
  // читает направления сегментов, а не то, кто их повернул. Материалы у трупа
  // личные (клоны, см. `cloneMaterials`) — поэтому и кожа модели тает вместе
  // с ним, а не гасит заодно всех живых.
  const boxes = [parts.head, parts.upperTorso, parts.lowerTorso,
    ...limbs.armL.meshes, ...limbs.armR.meshes,
    ...limbs.legL.meshes, ...limbs.legR.meshes];
  const skin = (useSkin() && wantSkin)
    ? makeSkin(group, firstPerson ? firstPersonKit(kit) : kit, boxes) : null;

  return { group, parts, limbs, hands, materials, skin, rig: skin ? SKIN_RIG : BOX_RIG };
}

/** Показать ровно один предмет из набора «в руках» по байту `held`.
 *
 *  Вынесено из `applyHands`, потому что трупу нужен только выбор предмета:
 *  веса поз ему считать не из чего — мёртвый не целится и не перезаряжается. */
export function showHeld(hands, held) {
  const isNade = (held & HELD_NADE) !== 0;
  const item = held & HELD_ITEM_MASK;
  const key = isNade ? `n${item}` : `g${item}`;
  if (hands.shown === key) return;
  hands.shown = key;
  for (const g of hands.guns) g.visible = false;
  for (const n of hands.nades) n.visible = false;
  const mesh = isNade ? hands.nades[item & 3] : hands.guns[item % 3];
  if (mesh) mesh.visible = true;
}

/** Поставить предмет «в руки» без анимации — для того, кому её считать не из
 *  чего. Сейчас потребитель один: труп. Он не зовёт `applyHands` (мёртвый не
 *  целится и не перезаряжается), а без неё пивот остаётся в нуле группы —
 *  то есть оружие лежало бы у трупа в ногах. */
export function restHands(hands, eye) {
  hands.pivot.position.set(POSE_HIP[0], eye + POSE_HIP[1], POSE_HIP[2]);
  hands.pivot.rotation.set(POSE_HIP[6], POSE_HIP[4], POSE_HIP[5]);
}

// ---- Что в руках: мировые модели оружия и гранат ----
//
// ⚠️ Это ОТДЕЛЬНЫЕ модели, а не те, что в `weapons.js::buildGun`, и это не
// дублирование по недосмотру. Вьюмодель нарочно искажена: она занимает угол
// экрана, поэтому у неё утрированные пропорции и своё положение относительно
// камеры. Мировая модель обязана быть примерно в масштабе человека — иначе
// у чужого в руках окажется бревно. Общая у них только роль «показать, чем
// вооружён», и связывать их одним кодом значило бы чинить одну, ломая другую.
//
// Главное требование к этим моделям — РАЗЛИЧИМОСТЬ СИЛУЭТА на дистанции боя.
// Деталей на 40 метрах не видно вовсе, читается только длина и наличие
// прицела: короткий обрубок — пистолет, длинный ствол — винтовка, длинный
// с трубой сверху — снайперка. Ради этого разница в длине сделана заметно
// больше реальной.
const GUN_BODY = new THREE.MeshStandardMaterial({ color: 0x23262b, roughness: 0.55, metalness: 0.45 });
const GUN_BARREL = new THREE.MeshStandardMaterial({ color: 0x121418, roughness: 0.4, metalness: 0.6 });

/** Длины стволов: пистолет / винтовка / снайперка (индексы `WeaponId`). */
const GUN_LEN = [0.22, 0.62, 0.92];

// ---- Точки хвата: где именно оружие лежит в кисти ----
//
// Координаты в системе МОДЕЛИ ствола (−Z вперёд, ноль — ствольная коробка).
// По ним `reachArm` доводит кисти: оружие по-прежнему висит на корпусе, а руки
// подстраиваются под него — см. комментарий у `makeHands`.
//
// ⚠️ Это ЦЕЛИ, а не углы шарниров. Задавать позу рук углами нельзя по той же
// причине, по которой ею не задаются ноги в приседе (см. `solveLeg`): поза
// оружия едет непрерывно (веса поз, наклон, питч, присед), и «правильные для
// стойки» углы на середине перехода отрывают кисть от рукояти.
const GRIP_R = [0, -0.055, 0.02];      // рукоять — одинакова у всех стволов

// Цевьё: за него берётся ВТОРАЯ рука. У пистолета его нет — он держится одной
// (решение владельца), поэтому `null`, и левая рука у него просто качается.
// ⚠️ Числа НЕ пропорциональны длине ствола: человек берётся за цевьё примерно
// в 25–30 см от рукояти, что винтовка, что снайперка. Растяни их по длине —
// и рука перестанет доставать до снайперки ровно так же, как доставала бы
// до винтовки.
// ⚠️ Винтовке 2026-08-06 цевьё ПРИДВИНУТО (0.27 → 0.23): при разнесённом
// вправо оружии левой кисти до прежней точки не хватало длины руки модели
// (0.545 м), ИК зажимала цель и рука вытягивалась в струну — см. `POSE_*`.
const GRIP_L = [null, [0, 0.0, -0.23], [0, -0.02, -0.21]];

// Куда левая рука уезжает на перезарядке: магазин под ствольной коробкой.
// Без этого она продолжала бы держать цевьё, то есть висела бы в воздухе
// поперёк опущенного оружия — «возится с магазином» из этого не читается.
const GRIP_MAG = [0, -0.13, -0.12];

// Граната лежит прямо в кулаке: точка хвата — её центр.
const GRIP_NADE = [0, 0, 0];

/** Примитивная мировая модель ствола (фолбэк): корпус + дуло, у снайперки ещё
 *  труба прицела. Модель смотрит вдоль −Z, как и всё остальное в этом файле.
 *  Используется, пока не догрузился GLB-шаблон или если он не загрузился вовсе.
 *
 *  Материалы приходят параметром, а не берутся из констант: труп растворяется
 *  прозрачностью, и красить ему общий материал нельзя — растаяли бы стволы
 *  у всех живых разом (см. `buildBody`). */
function buildPrimitiveWorldGun(idx, mats) {
  const g = new THREE.Group();
  const len = GUN_LEN[idx];
  const body = new THREE.Mesh(UNIT_BOX, mats.body);
  body.scale.set(0.05, 0.10, len * 0.55);
  body.position.z = -len * 0.22;
  g.add(body);

  const barrel = new THREE.Mesh(UNIT_BOX, mats.barrel);
  barrel.scale.set(0.032, 0.032, len * 0.6);
  barrel.position.z = -len * 0.62;
  g.add(barrel);

  // Рукоять — без неё ствол читается как палка, а не как оружие
  const grip = new THREE.Mesh(UNIT_BOX, mats.body);
  grip.scale.set(0.04, 0.13, 0.06);
  grip.position.set(0, -0.09, 0.02);
  g.add(grip);

  if (idx === 2) {                       // снайперка: труба прицела сверху
    const scope = new THREE.Mesh(UNIT_BOX, mats.barrel);
    scope.scale.set(0.045, 0.045, 0.26);
    scope.position.set(0, 0.09, -0.16);
    g.add(scope);
  } else if (idx === 1) {                // винтовка: магазин вниз
    const mag = new THREE.Mesh(UNIT_BOX, mats.barrel);
    mag.scale.set(0.035, 0.13, 0.05);
    mag.position.set(0, -0.09, -0.14);
    g.add(mag);
  }
  for (const m of g.children) m.castShadow = true;
  return g;
}

// ---- Мировые модели оружия из GLB (те же файлы, что во вьюмодели) ----
//
// Те же три GLB (CC0-пак Quaternius), что держит игрок от первого лица
// (`weapons.js`), но в масштабе человека и в системе координат мировой модели
// (−Z вперёд, начало у рукояти). Файлы грузятся отдельно от вьюмодели — браузер
// отдаёт их из HTTP-кэша, так что второй запрос почти бесплатен.
//
// ⚠️ Точки хвата `GRIP_R`/`GRIP_L` НЕ трогаем: масштаб подобран так, чтобы
// расстояние рукоять→цевьё модели совпало с существующими точками, а рукоять
// модели совмещается с `GRIP_R`. Тогда кисти (ИК `reachArm`) ложатся на место
// без пересчёта поз и без риска, что рука вытянется за пределы длины.
//   grip  — точка рукояти В КООРДИНАТАХ МОДЕЛИ (+X вперёд, как у пака);
//   scale — под расстояние рукоять→цевьё (винтовка/снайперка) или под длину
//           силуэта (пистолет держится одной рукой, цевья нет).
const WORLD_GUN = [
  { url: '/models/pistol.glb', scale: 0.121, grip: [-0.05, 0.0, 0],
    tint: { Wood: 0x6b4a2b, Metal: 0x3a3f47, Black: 0x141518, LightMetal: 0x6a7078 } },
  { url: '/models/assault_rifle.glb', scale: 0.15, grip: [0.12, -0.30, 0],
    tint: { Wood: 0x6b4a2b, DarkWood: 0x54381f, Black: 0x141518, Metal: 0x3a3f47, DarkMetal: 0x2a2e34 } },
  { url: '/models/sniper.glb', scale: 0.135, grip: [0.60, -0.10, 0],
    tint: { Black: 0x141518, DarkMetal: 0x2a2e34, LightMetal: 0x6a7078, Grey: 0x5a6144, Glass: 0x2a3a4a } },
];
const worldTpl = [null, null, null];        // готовые шаблоны (общие материалы)
const worldPending = [[], [], []];          // кого заменить, когда шаблон догрузится
const worldGunLoader = new GLTFLoader();

/** Собирает шаблон мировой модели: разворот +X→−Z, масштаб, совмещение рукояти
 *  с `GRIP_R`, перекраска частей по имени материала (OBJ-экспорт теряет
 *  палитру — см. md/graphics.md). Возвращает группу, готовую к `.clone()`. */
function buildWorldTemplate(idx, model) {
  const m = WORLD_GUN[idx], s = m.scale;
  model.scale.setScalar(s);
  model.rotation.set(0, Math.PI / 2, 0);          // +X (дуло) → −Z (вперёд)
  // transform(p) = (s·pz, s·py, −s·px); двигаем модель так, чтобы рукоять села в GRIP_R
  const gx = s * m.grip[2], gy = s * m.grip[1], gz = -s * m.grip[0];
  model.position.set(GRIP_R[0] - gx, GRIP_R[1] - gy, GRIP_R[2] - gz);
  model.traverse((o) => {
    if (!o.isMesh) return;
    o.castShadow = true;
    const name = o.material && o.material.name;
    if (name in m.tint) {
      o.material = o.material.clone();
      o.material.color.setHex(m.tint[name]);
      o.material.metalness = 0.4; o.material.roughness = 0.6;
    }
  });
  const tpl = new THREE.Group();
  tpl.add(model);
  return tpl;
}

WORLD_GUN.forEach((m, idx) => {
  worldGunLoader.load(m.url, (gltf) => {
    try {
      worldTpl[idx] = buildWorldTemplate(idx, gltf.scene);
      for (const cb of worldPending[idx]) cb(worldTpl[idx]);
      worldPending[idx].length = 0;
    } catch (e) { console.error('мировая модель оружия: сборка шаблона', idx, e); }
  }, undefined, (e) => console.error('мировая модель оружия: загрузка', idx, e));
});

/** Держатель одного ствола в руке чужого. Пока GLB не готов — внутри примитив;
 *  как только шаблон догрузится, у ЖИВОГО он заменяется на GLB (труп к этому
 *  моменту заведомо построен на готовом шаблоне — умирают не в первые мс).
 *
 *  `own` (труп) — клонировать материалы, чтобы их можно было гасить прозрачностью
 *  независимо от живых; клоны возвращаются в `mats` для списка растворения. */
function makeWorldGun(idx, mats, own, ghostMat) {
  const holder = new THREE.Group();
  const extra = [];
  // ⚠️ `ghostMat` — для СВОЕГО тела (`body.js`): его оружие должно оставаться
  // тенью, иначе поверх вьюмодели рисуется второй ствол. Применяется и при
  // асинхронной подмене примитива на GLB — иначе загрузившийся GLB «всплывает»
  // видимым (баг-урок: «два ствола в руках после показа оружия у ботов»).
  const applyGhost = (root) => { if (ghostMat) root.traverse((o) => { if (o.isMesh) o.material = ghostMat; }); };
  const attachTemplate = (tpl) => {
    const inst = tpl.clone(true);
    if (own) inst.traverse((o) => { if (o.isMesh) { o.material = o.material.clone(); extra.push(o.material); } });
    holder.add(inst);
    applyGhost(inst);
  };
  if (worldTpl[idx]) {
    attachTemplate(worldTpl[idx]);
  } else {
    const prim = buildPrimitiveWorldGun(idx, mats);
    holder.add(prim);
    applyGhost(prim);
    // Живого позже заменим на GLB; труп не апгрейдим (он строится на готовом
    // шаблоне, а если вдруг нет — примитив ему и останется, это доли процента).
    if (!own) worldPending[idx].push((tpl) => { holder.remove(prim); attachTemplate(tpl); });
  }
  return { group: holder, mats: extra };
}

/** Набор «в руках» для одного игрока: три ствола + четыре гранаты, из которых
 *  видна ровно одна вещь. Плодить меш на каждую смену оружия было бы дороже
 *  и давало бы заметный подтормаживание на первой смене у каждого противника.
 *
 *  Гранаты — GLB-модели из общего шаблона (nademodel.js), те же файлы, что
 *  летят по воздуху и лежат во вьюмодели: форма гранаты одна во всех руках. */
function makeHands(parent, mats, own = false, ghostMat = null) {
  const pivot = new THREE.Group();
  parent.add(pivot);
  const gunMats = [];        // материалы GLB-стволов (нужны трупу для растворения)
  const guns = [0, 1, 2].map((i) => {
    const { group, mats: gm } = makeWorldGun(i, mats, own, ghostMat);
    group.visible = false;
    pivot.add(group);
    for (const x of gm) gunMats.push(x);
    return group;
  });
  const nadeMats = [];       // материалы GLB-гранат (нужны трупу для растворения)
  const nades = [0, 1, 2, 3].map((i) => {
    const { group, mats: nm } = makeNade(i, { own, ghost: ghostMat });
    group.visible = false;
    pivot.add(group);
    for (const x of nm) nadeMats.push(x);
    return group;
  });
  return {
    pivot, guns, nades, gunMats, nadeMats, shown: null, ads: 0, reload: 0, draw: 0, nade: 0, sprint: 0,
    // Бит HELD_RELOAD прошлого кадра: по его гашению при взведённом
    // HELD_SPRINT слышно, что перезарядку оборвали бегом (см. applyHands).
    wasReload: false,
    // Хват: `two` — насколько предмет держится ДВУМЯ руками (вес, а не флаг:
    // смена пистолета на винтовку иначе дёргала бы левую руку скачком),
    // `gripR`/`gripL` — точки хвата уже в системе тела (их считает
    // `applyHands`, а доводит до них кисти `animateLimbs`).
    two: 0, gripR: new THREE.Vector3(), gripL: new THREE.Vector3(),
  };
}

// ---- Позы рук ----
//
// Поза — это положение и поворот `hands.pivot` относительно тела: [x, y от
// уровня глаз, z, поворот по X сверх питча, по Y, по Z]. Позы СМЕШИВАЮТСЯ
// весами (см. `applyHands`), а не переключаются: переключение давало бы
// щелчок при каждом входе в прицел, а вес сам по себе даёт плавный переход
// и не требует знать длительность анимации.
//
// ⚠️ Именно поэтому длительности перезарядки здесь НЕТ, хотя её так и тянет
// добавить. Сервер шлёт только факт «перезаряжается», а сколько это длится,
// зависит от ствола (1.1…2.4 с). Заведи здесь копию этих чисел — и получишь
// ТРЕТЬЮ копию баланса оружия (после `weapons.rs` и `weapons.js`), которая
// молча разъедется при первой же правке. Вес, ползущий к 1 и обратно к 0,
// растягивается на любую длительность сам.
//
// ⚠️ Все позы ПЕРЕСЧИТАНЫ под хват руками (`reachArm`), и «подвинуть оружие
// чуть красивее» больше нельзя без проверки: точки хвата обязаны оставаться
// в пределах длины руки от плеча, иначе кисть отрывается от рукояти и рука
// вытягивается в струну. Раньше это было всё равно — руки не держали ничего,
// и оружие висело у бедра на x = 0.26, то есть ровно на краю торса, откуда
// и росло «оружие выпирает из корпуса». Проверяется расчётом, а не глазами
// (см. CLAUDE.md 2.3.5): длина руки 0.67, дотягивается кисть на 0.61.
//
// ⚠️ **ПЕРЕСЧИТАНЫ 2026-08-06** по жалобе владельца «оружие плохо ложится
// в руки и торчит из корпуса». Причин было ДВЕ, и обе видно только рендером
// стенда (`bash tools/shot.sh stand` / `ads`), а не в игре:
//
// 1. Оружие висело на ОСИ ТЕЛА (`x` ≈ 0) и разворотом уводило приклад ВЛЕВО
//    (отрицательный `[4]`). Модель ствола растёт от рукояти назад сантиметров
//    на двадцать, торс глубиной 0.36 — значит приклад и магазин физически
//    находились внутри груди. Теперь оружие смещено ВПРАВО, а разворот
//    ПОЛОЖИТЕЛЬНЫЙ: приклад уходит к правому плечу, дуло — чуть влево,
//    то есть ровно так, как его держит человек.
// 2. Левая кисть НЕ ДОСТАВАЛА до цевья: от левого плеча до него выходило
//    0.563 м при пределе руки модели 0.545 (`armSegs`: 0.251 + 0.244 + 0.100/2).
//    ИК честно зажимала цель по длине — и обе руки вытягивались в струну,
//    что и читается как «не держит, а тычет». Все числа ниже проверены этим
//    расчётом; двигая их, считать заново, иначе руки снова выпрямятся.
//
// ⚠️ Седьмое число — ПОСТОЯННЫЙ наклон ствола (рад, вниз положительный).
// Раньше наклон был только от питча (`[3] × pitch`), поэтому смотрящий прямо
// держал оружие строго горизонтально в любой позе, включая «от бедра» и бег.
// Человек так не носит: у опущенного оружия дуло смотрит в землю.
const POSE_HIP    = [0.18, -0.36, -0.16, 0.35, 0.35, 0.10, -0.30];
const POSE_ADS    = [0.10, -0.02, -0.20, 1.00, 0.00, 0.00, 0.00];
const POSE_RELOAD = [0.16, -0.38, -0.20, 0.20, 0.25, 0.35, -0.25];
const POSE_DRAW   = [0.20, -0.48, -0.18, 0.00, 0.20, 0.35, -0.45];
// Граната отведена к бедру и назад — замах. Ближе к плечу её класть нельзя:
// рука сложилась бы вдвое и кулак ушёл бы в грудь.
const POSE_NADE   = [0.24, -0.42, -0.14, 0.00, -0.20, 0.00, 0.00];
// Бег: оружие прижато к корпусу и развёрнуто поперёк — то же движение, что
// у своей вьюмодели (`weapons.js::GUN_SPRINT`), и это важнее красоты. Именно
// по этой позе противник понимает, что человек СЕЙЧАС выстрелить не может:
// сервер ему этого не даст (`room.rs::fire`). Разъедется с вьюмоделью —
// и на экране будет два разных правила для одного и того же.
const POSE_SPRINT = [0.20, -0.40, -0.14, 0.05, 0.35, 0.50, -0.50];
// Лёжа: ствол у самой земли перед лицом, на руках-подпорках. Смешивается
// ПОСЛЕДНИМ и по весу позы, а не по кнопке — потому что это не то, что игрок
// делает руками, а то, где вообще находится его тело.
const POSE_PRONE  = [0.10, -0.22, 0.12, 0.65, -0.10, 0.00, 0.00];

/** Скорость, с которой веса поз ползут к цели (1/с). Больше — резче переход.
 *  Прицеливание нарочно быстрее остального: вскидывание оружия — самое
 *  информативное движение противника, и запаздывать оно не должно. */
const POSE_SPEED = 9;
const ADS_SPEED = 14;

function ease(cur, target, rate, dt) {
  return cur + (target - cur) * Math.min(1, rate * dt);
}

/** Линейное смешивание двух поз. */
function mixPose(out, pose, w) {
  for (let i = 0; i < POSE_LEN; i++) out[i] += (pose[i] - out[i]) * w;
}

/** Сколько чисел в позе: x, y, z, доля питча, поворот Y, поворот Z и
 *  ПОСТОЯННЫЙ наклон ствола. Именованной длиной, а не «6» по месту:
 *  добавленное седьмое число забылось бы ровно в одном из двух циклов. */
const POSE_LEN = 7;
const _pose = [0, 0, 0, 0, 0, 0, 0];

// ---- Кисти на оружии: двухзвенная обратная кинематика руки ----
//
// ⚠️ Почему именно так, а не «повесить ствол на кисть». Оружие по-прежнему
// висит на группе ТЕЛА (см. `makeHands`): через кисть его положение зависело
// бы от анимации ходьбы, и ствол болтался бы вместе с махом руки. Человек
// ведёт оружие корпусом, а руки подстраиваются — здесь буквально так: поза
// ставит ствол, а кисти доводятся до его точек хвата.
//
// До этого шага «доводятся» было только в комментарии: руки стояли под
// фиксированными углами (`ARM_BASE`/`ELBOW_BASE` плюс поправки на ADS), и до
// оружия им было полметра. Поэтому ствол читался как выросший из груди.
//
// Задача та же, что у ног в приседе (`solveLeg`), только в 3D: даны плечо
// и цель, найти повороты плеча и локтя. Разница — плоскости нет, поэтому
// углы считаются векторами, а не арктангенсами, и результат кладётся
// кватернионами.
const _u = new THREE.Vector3(), _w = new THREE.Vector3(), _pole = new THREE.Vector3();
const _upper = new THREE.Vector3(), _lower = new THREE.Vector3(), _end = new THREE.Vector3();
const _qUp = new THREE.Quaternion(), _qLo = new THREE.Quaternion(), _qInv = new THREE.Quaternion();
const _qId = new THREE.Quaternion();
const _hinge = new THREE.Vector3();
const _ax = new THREE.Vector3(), _ay = new THREE.Vector3(), _az = new THREE.Vector3();
const _basis = new THREE.Matrix4();

/** Куда смотрит ладонь модели относительно шарнира рига.
 *
 *  ⚠️ Не догадка, а следствие того, как `skinbody.js::skin.fix` переносит
 *  поворот шарнира на кость. В позе привязки рука модели лежит вдоль +X,
 *  ладонь смотрит вниз; `fix` доворачивает ось кости в −Y минимальным
 *  поворотом (вокруг Z на −90°), и мировая «вниз» уезжает при этом в −X
 *  системы шарнира. То есть НОРМАЛЬ ЛАДОНИ — это локальная −X шарнира,
 *  а локальную +X мы как раз и задаём базисом (`segQuat`). Значит, чтобы
 *  ладонь легла на оружие, ось +X предплечья обязана смотреть ОТ оружия. */
const _gunRight = new THREE.Vector3();
const _sideAxis = new THREE.Vector3();

/** Довести кисть до точки `tgt` (система тела, как и `arm.root.position`).
 *
 *  `side` — +1 правая рука, −1 левая: по нему выбирается, куда уводить локоть.
 *  `ads` — вес прицеливания: у вскинутого оружия локоть поднимается вбок.
 *  `w` — вес всей позы: 0 оставляет руку как есть (она качается при ходьбе),
 *  1 полностью отдаёт её хвату. Промежуточные значения нужны при смене
 *  пистолета на винтовку — вторая рука приходит на цевьё плавно.
 *
 *  ⚠️ Целимся СЕРЕДИНОЙ кисти, а не запястьем: длина последнего звена входит
 *  в расчёт наполовину. Тогда коробка кулака охватывает рукоять, а не
 *  упирается в неё торцом и не проходит сквозь.
 *
 *  Недостижимую цель зажимаем по длине руки — рука вытягивается ровно на неё
 *  и продолжает указывать на рукоять. Это осознанно: так ведёт себя и живая
 *  рука, а альтернатива (тянуть плечо за кистью) развалила бы силуэт, который
 *  обязан совпадать с хитбоксом. */
function reachArm(arm, lens, side, tgt, ads, w, palm) {
  if (w <= 0.001) return;
  const L1 = lens[0];
  const L2 = lens[1] + lens[2] * 0.5;
  const s = arm.root.position;

  _u.set(tgt.x - s.x, tgt.y - s.y, tgt.z - s.z);
  let r = _u.length();
  if (r < 1e-4) return;
  _u.divideScalar(r);
  r = Math.min(Math.max(r, Math.abs(L1 - L2) + 1e-3), L1 + L2 - 1e-3);

  // Локоть уводится в сторону «полюса»: вниз и наружу-назад у опущенного
  // оружия, вбок и выше — у вскинутого (то самое «крыло» стрелка в прицеле).
  // Берём составляющую полюса, перпендикулярную линии «плечо→цель»: только
  // она и решает, куда согнётся рука.
  _pole.set(side * mix(0.45, 0.85, ads), mix(-1, -0.25, ads), mix(0.30, 0.10, ads));
  _w.copy(_pole).addScaledVector(_u, -_pole.dot(_u));
  if (_w.lengthSq() < 1e-6) _w.set(_u.z, 0, -_u.x);   // полюс совпал с целью
  _w.normalize();

  // Угол между плечом и линией «плечо→цель» — теорема косинусов, как в
  // `solveLeg`. Аргумент зажат: без этого `acos` на пределе достижимости
  // возвращает NaN, а NaN в повороте гасит весь меш целиком.
  const cosA = Math.min(1, Math.max(-1, (L1 * L1 + r * r - L2 * L2) / (2 * L1 * r)));
  const a = Math.acos(cosA);
  _upper.copy(_u).multiplyScalar(Math.cos(a)).addScaledVector(_w, Math.sin(a));

  // Предплечье смотрит из локтя в (зажатую) цель.
  _end.copy(_u).multiplyScalar(r).add(s);
  _lower.copy(_end).addScaledVector(_upper, -L1).sub(s).normalize();

  // ---- Поворот сегментов: НАПРАВЛЕНИЯ МАЛО ----
  //
  // ⚠️ Здесь стояло `setFromUnitVectors(DOWN, dir)` — «минимальный поворот,
  // разворачивающий сегмент вдоль направления». Он задаёт ось кости и НЕ
  // ЗАДАЁТ поворот вокруг неё: у коробки это незаметно (она почти круглая
  // в сечении), а у человеческой модели именно этот поворот решает, куда
  // смотрит ЛАДОНЬ. Доставался он произвольно и прыгал вслед за целью —
  // отсюда жалоба владельца «оружие плохо ложится в руки»: кисти лежали
  // на рукояти то ребром, то тылом, а пальцы торчали в стороны.
  //
  // ⚠️ Это ТА ЖЕ ошибка, на которой уже горели ступни (md/models.md 2.3.10),
  // и лечение то же: строим ПОЛНЫЙ базис. Вдоль сегмента — локальная −Y,
  // по оси сгиба локтя — локальная +X (именно вокруг неё риг гнёт руку:
  // `animateLimbs` пишет `joints[1].rotation.x`). Ось сгиба перпендикулярна
  // плоскости «плечо → цель × полюс», то есть той самой плоскости, в которой
  // рука и складывается, — значит рука получается анатомичной, а не
  // вывернутой.
  _hinge.crossVectors(_u, _w).normalize();
  segQuat(_qUp, _upper, _hinge);
  // ---- Предплечье: пронация «ладонью на оружие» ----
  //
  // У предплечья свой поворот вокруг оси, и это не вольность, а анатомия:
  // человек кладёт ладонь на цевьё, не разворачивая при этом локоть. Ось
  // ладони берём ОТ САМОГО ОРУЖИЯ (`palm` — его локальная +X в системе тела),
  // а не константой: тогда обе кисти смотрят на ствол с двух сторон в любой
  // позе — от бедра, в прицеле, лёжа, — и подбирать угол под каждую позу
  // не приходится. Знак зеркальный: правая ладонь смотрит влево, левая
  // вправо, иначе одна из них ляжет на оружие тылом.
  _sideAxis.copy(palm).multiplyScalar(side);
  segQuat(_qLo, _lower, _sideAxis);
  // Шарнир локтя задан ОТНОСИТЕЛЬНО плеча, поэтому снимаем поворот плеча.
  _qLo.premultiply(_qInv.copy(_qUp).invert());

  arm.joints[0].quaternion.slerp(_qUp, w);
  arm.joints[1].quaternion.slerp(_qLo, w);
  // Кисть продолжает предплечье: кулак, сжимающий рукоять, и есть прямое
  // продолжение руки. Пальцев у модели нет вовсе (в скелете одна кость
  // `hand_*`), поэтому «сжать» их нельзя — но развёрнутая пронацией ладонь
  // ложится на оружие, а не смотрит мимо него.
  arm.joints[2].quaternion.slerp(_qId, w);
}

/** Поворот сегмента: вдоль него — локальная −Y, по `hinge` — локальная +X.
 *
 *  Отдельной функцией, потому что зовётся дважды за руку и обязана делать
 *  это ОДИНАКОВО: разъедься базисы плеча и предплечья — и локоть согнётся
 *  не в своей плоскости, то есть рука вывернется. */
function segQuat(out, dir, hinge) {
  _ay.copy(dir).multiplyScalar(-1);
  // Ось сгиба может оказаться не строго перпендикулярной сегменту (клампы,
  // вырожденные случаи) — отнимаем продольную составляющую.
  _ax.copy(hinge).addScaledVector(_ay, -hinge.dot(_ay));
  if (_ax.lengthSq() < 1e-8) _ax.set(_ay.y, -_ay.x, 0);
  _ax.normalize();
  _az.crossVectors(_ax, _ay);
  _basis.makeBasis(_ax, _ay, _az);
  return out.setFromRotationMatrix(_basis);
}

/** Цепочка сегментов на шарнирах: каждый висит вдоль −Y от своего шарнира.
 *
 *  Вложенность (плечо → предплечье → кисть) нужна затем, чтобы поворот
 *  верхнего шарнира тащил за собой всю конечность — иначе анимацию пришлось
 *  бы считать тригонометрией на каждый сегмент вручную.
 *
 *  Длины здесь НЕ задаются: они зависят от позы и ставятся каждый кадр
 *  в `setLimb`. */
function makeLimb(parent, mat, halfs) {
  const joints = [], meshes = [];
  let pivot = new THREE.Group();
  parent.add(pivot);
  for (const [hx, hz, zoff] of halfs) {
    const m = makePart(mat);
    m.scale.set(hx * 2, 1, hz * 2);
    m.position.z = zoff;
    pivot.add(m);
    joints.push(pivot);
    meshes.push(m);
    const next = new THREE.Group();
    pivot.add(next);
    pivot = next;
  }
  return { root: joints[0], joints, meshes };
}

/** Длины сегментов конечности. Пересчитываются каждый кадр: присед и лёжа
 *  укорачивают ноги, а поворот шарниров при этом не должен «шириться» —
 *  поэтому меняем длины, а не масштабируем корень группы. */
function setLimb(limb, lens) {
  for (let i = 0; i < lens.length; i++) {
    limb.meshes[i].scale.y = lens[i];
    limb.meshes[i].position.y = -lens[i] / 2;
    if (limb.joints[i + 1]) limb.joints[i + 1].position.y = -lens[i];
  }
}

/** Полоска HP над ботом: один спрайт с canvas-текстурой.
 *
 *  Фон и заливка рисуются вместе на одном холсте, а не двумя спрайтами —
 *  два разъезжались бы под углом обзора. `depthTest:false` нужен, чтобы
 *  полоска не тонула в собственном теле; за это же приходится вручную
 *  проверять перекрытие стеной (см. `occluded`).
 */
function makeHpBar() {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 10;
  const ctx = canvas.getContext('2d');
  const tex = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: tex, depthTest: false, transparent: true,
  }));
  sprite.scale.set(1.0, 0.16, 1);
  sprite.renderOrder = 10;
  return { sprite, canvas, ctx, tex, shown: -1 };
}

function drawHpBar(bar, hp) {
  if (bar.shown === hp) return;      // перерисовываем только при изменении
  bar.shown = hp;
  const frac = Math.max(0, Math.min(1, hp / 100));
  const c = bar.ctx, W = bar.canvas.width, H = bar.canvas.height;
  c.clearRect(0, 0, W, H);
  c.fillStyle = '#111'; c.fillRect(0, 0, W, H);
  c.fillStyle = frac > 0.5 ? '#22c55e' : frac > 0.25 ? '#eab308' : '#dc2626';
  c.fillRect(1, 1, (W - 2) * frac, H - 2);
  bar.tex.needsUpdate = true;
}

// ---- Перекрытие: что нельзя показывать поверх стены ----
//
// ⚠️ БАГ-УРОК. Полоска HP и ник рисуются с `depthTest:false`, то есть ПОВЕРХ
// всего — иначе полоска утонула бы в теле, а ник в стене за спиной. Обратная
// сторона: они же светят сквозь стены. Само по себе PVS-culling сервера это
// почти лечит (врага за стеной в снапшоте просто нет), но не полностью:
// VIS_GRACE = 500 мс и упреждение видимости (до LEAD_MAX = 0.45 с, плюс шаг
// вбок и прыжок — см. CLAUDE.md 2.9) оставляют окно, в котором цель уже
// за укрытием, а данные ещё идут. ⚠️ Окно это стало ШИРЕ, чем было при общей
// константе 0.25 с, так что проверка перекрытия здесь теперь ещё нужнее.
// Раньше в этом окне над стеной висела
// полоска HP — ровно то, что заметил владелец. Поэтому проверяем сами.
const _camPos = new THREE.Vector3(), _dir = new THREE.Vector3();

function occluded(x, y, z) {
  camera.getWorldPosition(_camPos);
  _dir.set(x - _camPos.x, y - _camPos.y, z - _camPos.z);
  const dist = _dir.length();
  if (dist < 0.5) return false;
  _dir.divideScalar(dist);

  // Допуск 0.3 м: цель может стоять вплотную к стене, и попадание в саму
  // стену на последних сантиметрах перекрытием считать нельзя.
  //
  // ⚠️ Считаем СВОИМ лучом по коробкам карты (player.js::rayWalls, порт
  // hit.rs), а не рейкастом three.js. Причин две. Первая — цена: этот луч
  // пускается на КАЖДОГО видимого чужого КАЖДЫЙ кадр, и по геометрии
  // импортированной карты рейкаст three.js стоил ~0.2 мс за штуку, то есть
  // с десятком ботов съедал больше кадрового бюджета, чем вся физика.
  // Вторая — правильность: сервер решает видимость ровно этой формулой,
  // а меш карты рисует только внешнюю оболочку (см. world.js) и про
  // внутренние грани не знает.
  if (rayWalls(_camPos.x, _camPos.y, _camPos.z, _dir.x, _dir.y, _dir.z, dist) < dist - 0.3) return true;

  // Дым: облако на линии взгляда прячет так же, как стена. Без этого
  // полоска висела бы посреди дыма, обесценивая саму дымовую гранату.
  if (!smokes.length) return false;
  const t = clock.elapsedTime;
  const ax = _camPos.x, ay = _camPos.y, az = _camPos.z;
  const bx = _dir.x * dist, by = _dir.y * dist, bz = _dir.z * dist;
  const ab2 = bx * bx + by * by + bz * bz;
  if (ab2 < 1e-4) return false;
  for (const s of smokes) {
    const age = t - s.born;
    // 0.4 — время выпуска (SMOKE_FILL в grenades.js): дым встаёт почти сразу,
    // и прежние 0.8 с давали окно, в котором облако уже стеной, а метки
    // сквозь него ещё видны.
    if (age < 0.4 || age > s.life - 0.5) continue;   // ещё не сгустился / уже развеялся
    let u = ((s.cx - ax) * bx + (s.cy - ay) * by + (s.cz - az) * bz) / ab2;
    u = Math.max(0, Math.min(1, u));
    // ⚠️ Облако — низкий широкий ЦИЛИНДР (радиус 12.8 м, высота ~5): шаром
    // того же радиуса оно прятало бы стоящего на террасе десятью метрами
    // выше, где дыма нет. Горизонталь — по радиусу, вертикаль — по границам.
    const py = ay + by * u;
    if (py < s.y0 || py > s.top) continue;
    const dx = ax + bx * u - s.cx, dz = az + bz * u - s.cz;
    if (dx * dx + dz * dz < s.rad * s.rad) return true;
  }
  return false;
}

function createRemote(id, isNpc, ally = false) {
  const { group, parts, limbs, hands, skin, rig } = buildBody(isNpc, false, null, true, false, ally);

  // Нос-указатель: куда игрок смотрит. Тело теперь шире в плечах, чем в
  // глубину, и курс читается и без него, но в упор так однозначнее.
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.07, 0.18, 8),
    new THREE.MeshStandardMaterial({ color: 0xfbbf24 }));
  nose.rotation.x = Math.PI / 2;
  // ⚠️ У человеческой модели носа-указателя нет: курс читается по лицу, плечам
  // и стволу в руках, а жёлтый конус, торчащий изо лба, выглядел бы поломкой.
  // Он остаётся ровно для коробочного тела, где кроме него курс сказать нечем.
  nose.visible = !skin;
  group.add(nose);

  const label = makeNickLabel(nicks.get(id) || `#${id}`);
  group.add(label);

  // Полоска HP — только над ботами. У живых игроков её нет намеренно: чужое
  // здоровье в шутере не показывают, это разведданные, которые игрок должен
  // добывать сам. Бот же — мишень, и по нему полоска и есть обратная связь.
  const hpBar = isNpc ? makeHpBar() : null;
  if (hpBar) group.add(hpBar.sprite);

  scene.add(group);
  const r = {
    group, parts, limbs, nose, label, hpBar, hands, skin, rig, npc: isNpc, ally,
    // Фаза шага у каждого своя: иначе шестеро ботов маршировали бы нога в ногу.
    phase: Math.random() * Math.PI * 2,
    // То же и у часов покоя (дыхание, перенос веса) — со случайного места.
    idle: Math.random() * 10,
    speed: 0, prevX: 0, prevZ: 0, tracked: false, back: false,
    // ⚠️ false, а не true: увиденный впервые уже мёртвым труп не роняем —
    // смерти мы не видели, и тело взялось бы из ниоткуда.
    wasAlive: false,
    lastEye: 1.7, lastLean: 0, lastHeld: 0,
  };
  remotes.set(id, r);
  return r;
}

/** Ставит коробку в интервал [y0,y1] с полуразмерами hx/hz. Пустую прячет.
 *
 *  `cz` — смещение вперёд-назад по локальной оси тела (вперёд — это −Z).
 *  Стоя оно нулевое у всех коробок, а в позах именно оно кладёт тело: лёжа
 *  торс и таз уезжают назад от головы. Зеркало поля `HitBox::cz` в hit.rs. */
function placePart(mesh, y0, y1, hx, hz, lean, eye, cz = 0) {
  const h = y1 - y0;
  mesh.visible = h > 1e-4;
  if (!mesh.visible) return;
  mesh.scale.set(hx * 2, h, hz * 2);
  const cy = y0 + h / 2;
  // Наклон уносит вбок верх тела, а не всё целиком — зеркало `lean_weight`
  // в hit.rs: человек перегибается в поясе, ноги остаются на месте.
  mesh.position.set(lean * leanWeight(cy, eye), cy, cz);
}

/** Доля наклона на высоте `y` — построчно как `hit.rs::lean_weight`. */
function leanWeight(y, eye) {
  const pivot = eye - PELVIS_BOTTOM;
  return Math.max(0, Math.min(1, (y - pivot) / PELVIS_BOTTOM));
}

/** Раскладка тела по высоте глаз.
 *
 *  Корпус — построчно как `hit.rs::hitboxes`. Конечности пристёгиваются
 *  к тем же регионам: ноги делят регион ног, руки висят у верха торса. */
// Длины сегментов пересчитываются каждый кадр на каждого игрока — держим
// переиспользуемые массивы, чтобы не сорить в сборщик мусора по десятку
// коротких массивов на кадр (та же причина, по которой геометрия общая).
const _lens = [0, 0, 0];
// Куда фактически попала лодыжка — для клампа ступни по полу в poseLegs.
const _foot = new THREE.Vector3();

export function layoutBody(r, eye, lean, pitch = 0) {
  const cut = (y) => Math.max(y, 0);   // группа стоит ступнями в 0
  const leanAt = (y) => lean * leanWeight(y, eye);
  const kneel = kneelAmount(eye);
  // Пропорции конечностей: у коробок свои, у человеческой модели свои
  // (см. BOX_RIG). Коробки корпуса ниже от рига НЕ зависят — они зеркало hit.rs.
  const rig = r.rig || BOX_RIG;
  // Наклон и питч понадобятся `syncSkin` в конце кадра — кладём их сюда, чтобы
  // не тащить через `animateLimbs` двумя лишними аргументами.
  r._lean = lean; r._pitch = pitch;
  const prone = proneAmount(eye);
  // Веса нужны и анимации конечностей — она идёт отдельным проходом
  // (`animateLimbs`) уже после того, как раскладка посчитана.
  r.kneel = kneel; r.prone = prone;

  // Корпус. Стоя это прежняя вертикальная стопка; лёжа те же коробки
  // раскладываются НАЗАД от головы, потому что голова остаётся в точке глаз —
  // там же, где сидит камера и откуда сервер пускает пулю.
  const headY0 = mix(cut(eye - HEAD_BOTTOM), PRONE_HEAD_Y0, prone);
  const headY1 = mix(cut(eye + HEAD_TOP), PRONE_HEAD_Y1, prone);
  placePart(r.parts.head, headY0, headY1,
    HEAD_HALF, mix(HEAD_HALF, PRONE_HEAD_HALF_Z, prone), lean, eye);
  placePart(r.parts.upperTorso,
    mix(cut(eye - TORSO_BOTTOM), 0, prone), mix(cut(eye - HEAD_BOTTOM), PRONE_TORSO_Y1, prone),
    TORSO_HALF_X, mix(TORSO_HALF_Z, PRONE_TORSO_HALF_Z, prone), lean, eye,
    PRONE_TORSO_CZ * prone);
  placePart(r.parts.lowerTorso,
    mix(cut(eye - PELVIS_BOTTOM), 0, prone), mix(cut(eye - TORSO_BOTTOM), PRONE_PELVIS_Y1, prone),
    PELVIS_HALF_X, mix(PELVIS_HALF_Z, PRONE_PELVIS_HALF_Z, prone), lean, eye,
    PRONE_PELVIS_CZ * prone);

  // ⚠️ Коробки корпуса при человеческой модели гасим КАЖДЫЙ кадр, а не один
  // раз при подмене: `placePart` выше сама включает видимость по высоте
  // коробки, и погашенный однажды торс возвращался бы на следующем кадре.
  //
  // ⚠️ Из графа сцены их при этом НЕ убираем, и это не лень: по телам чужих
  // идёт локальный рейкаст выстрела (`bodiesAlong` — декаль, брызги, конец
  // трассера), и он обязан идти по той же геометрии, по которой считает
  // попадания сервер. Кожа модели уже её ширины и вылезает плечами наружу —
  // стреляй мы по ней, картинка попадания разошлась бы с уроном. Поэтому
  // коробки остаются невидимым прокси, а меши модели из рейкаста исключены
  // (`skinbody.js::attach`).
  if (r.skin && r.skin.ready) {
    r.parts.head.visible = false;
    r.parts.upperTorso.visible = false;
    r.parts.lowerTorso.visible = false;
  }

  // Крен вслед за наклоном (см. LEAN_ROLL). Знак — как у камеры своего игрока:
  // положительный `lean` уводит тело в +X, и завалиться оно обязано туда же.
  // Таз не кренится вовсе: человек перегибается в поясе, и это та же точка
  // опоры, из которой растёт `leanWeight`.
  const roll = -(lean / LEAN_OFFSET) * LEAN_ROLL;
  r.parts.head.rotation.z = roll;
  r.parts.upperTorso.rotation.z = roll;
  r.parts.lowerTorso.rotation.z = roll * 0.35;

  // Ноги. Длина постоянная (см. LEG_SEGS) — присед их СКЛАДЫВАЕТ, а не
  // втягивает, — поэтому здесь только точка крепления: таз стоя и присев,
  // а лёжа — то место, где таз оказался позади головы.
  const hipY = cut(eye - rig.hipDrop);
  const legY = mix(hipY, PRONE_HIP_Y, prone);
  const legZ = PRONE_HIP_CZ * prone;
  setLimb(r.limbs.legL, rig.legSegs);
  setLimb(r.limbs.legR, rig.legSegs);
  r.limbs.legL.root.position.set(-rig.legSpan + leanAt(legY), legY, legZ);
  r.limbs.legR.root.position.set(rig.legSpan + leanAt(legY), legY, legZ);

  // Руки: длина абсолютная, как у головы и торса (приседая, человек
  // складывает ноги, а не укорачивает руки). Но в вертикальных позах плечо
  // может опуститься почти к полу, и полная рука ушла бы под землю —
  // поджимаем. Лёжа поджимать не надо вовсе: рука там лежит горизонтально
  // и в пол не упирается.
  const shoulderY = cut(eye - rig.shoulderDrop);
  const armK = mix(Math.min(1, shoulderY / rig.armFull), 1, prone);
  const armX = rig.armX;
  const armY = mix(shoulderY, PRONE_SHOULDER_Y, prone);
  const armZ = PRONE_SHOULDER_CZ * prone;
  // Длины руки нужны ещё и хвату (`reachArm`) — он идёт отдельным проходом,
  // как и вся анимация конечностей. Массив заводим на игроке один раз: труп
  // собирается той же функцией, но своего поля не приносит.
  const armLens = r.armLens || (r.armLens = [0, 0, 0]);
  for (let i = 0; i < 3; i++) _lens[i] = armLens[i] = rig.armSegs[i] * armK;
  r.limbs.armL.root.position.set(-armX + leanAt(armY), armY, armZ);
  r.limbs.armR.root.position.set(armX + leanAt(armY), armY, armZ);
  setLimb(r.limbs.armL, _lens);
  setLimb(r.limbs.armR, _lens);

  // Нос, ник и полоска HP есть только у живого: труп собирается той же
  // функцией, но ему они не положены (мёртвый не «смотрит», а подписывать
  // тело именем — это уже интерфейс, которого в CS нет).
  if (r.nose) r.nose.position.set(lean, eye, -(HEAD_HALF + 0.09));
  if (!r.label) return;
  const top = eye + HEAD_TOP;
  if (r.hpBar) {
    // Ник поднимаем выше обычного: иначе он наезжает на полоску HP
    r.hpBar.sprite.position.y = top + 0.15;
    r.label.position.y = top + 0.62;
  } else {
    r.label.position.y = top + 0.35;
  }
}

// ---- Ходьба ----
//
// ⚠️ Фаза шага накапливается из ФАКТИЧЕСКИ пройденного пути, а не из времени.
// Иначе ноги «скользят по льду»: переступают в своём ритме, пока тело едет
// в чужом, — и это первое, что глаз ловит в плохой анимации. Шаг ровно 2.2 м,
// та же величина, что у звука шагов (`player.js::movementEffects` и
// `sound.rs::STEP_DIST`), поэтому нога касается пола в такт слышимому шагу.
//
// Экспортируется ради своего тела от первого лица (`body.js`): оно считает
// фазу шага той же формулой, и заводить там четвёртую копию числа нельзя.
export const STEP_DIST = 2.2;

/** Порог, за которым считается, что человек ПЯТИТСЯ, м/с. С гистерезисом:
 *  между −0.3 и +0.3 признак не меняется вовсе. Без мёртвой зоны идущий
 *  боком (проекция на взгляд болтается около нуля) дёргал бы ногами
 *  туда-сюда каждый кадр. */
const BACK_SPEED = 0.3;

/** Скорость вдоль взгляда: >0 — идёт вперёд, <0 — пятится.
 *
 *  Фронт тела — локальная −Z (нос-указатель, `placePart`), поэтому вектор
 *  взгляда по курсу `yaw` это `(−sin, −cos)`. */
export function forwardSpeed(vx, vz, yaw) {
  return -(vx * Math.sin(yaw) + vz * Math.cos(yaw));
}

/**
 * Продвинуть фазу шага на пройденный путь — С УЧЁТОМ НАПРАВЛЕНИЯ.
 *
 * ⚠️ Пятящийся получает фазу, идущую НАЗАД, а не зеркальный размах. Разница
 * принципиальная: размах — это амплитуда, и её переворот в момент смены
 * направления даёт щелчок (нога прыгает из переднего положения в заднее).
 * Реверс же фазы непрерывен — цикл просто начинает отматываться обратно,
 * ровно как у человека, который затормозил и пошёл назад.
 *
 * ⚠️ Идущий БОКОМ считается идущим вперёд, и это осознанный размен: честно
 * было бы разложить шаг по направлению движения, а это уже вторая ось
 * качания (`rotation.z`) и другая анимация. Боком в шутере ходят коротко,
 * а вот пятятся постоянно — оно и починено.
 *
 * @param dist пройденный за кадр путь, м (всегда положительный)
 * @param fwd  скорость вдоль взгляда (`forwardSpeed`)
 */
export function stepPhase(r, dist, fwd) {
  if (fwd > BACK_SPEED) r.back = false;
  else if (fwd < -BACK_SPEED) r.back = true;
  const step = (dist / STEP_DIST) * Math.PI * (r.back ? -1 : 1);
  const T = Math.PI * 2;
  r.phase = ((r.phase + step) % T + T) % T;
}

// Базовая поза рук: чуть вперёд и согнуты в локте — игрок держит ствол.
// Ствола у чужих пока нет, но руки, висящие плетьми, читаются как труп.
const ARM_BASE = 0.30, ELBOW_BASE = 0.80;

// ⚠️ Какую долю питча отыгрывает голова. Целиком поворачивать её НЕЛЬЗЯ:
// коробка головы — зеркало хитбокса, а на сервере он осевой и питч на него
// не влияет вовсе. При доле 0.35 и предельном питче 1.6 рад голова кренится
// на 32°, и её силуэт раздаётся примерно на 4 см — заметить это в бою нельзя,
// а неподвижная голова при вскинутых руках читается как поломка.
// Руки такого ограничения не имеют: сервер о них не знает совсем.
const HEAD_PITCH_SHARE = 0.35;

// ⚠️ Крен корпуса при наклоне Q/E — ЧИСТАЯ КОСМЕТИКА, как и `HEAD_PITCH_SHARE`.
//
// Сам наклон авторитетен и уже виден: `lean` уносит верх тела вбок (см. 2.3.1),
// и хитбоксы уезжают ровно туда же. Но одного смещения глазу мало — коробки
// остаются строго вертикальными, и человек читается не как выглянувший
// из-за угла, а как проехавший вбок целиком. Крен добавляет ровно недостающее.
//
// Величина — зеркало камеры своего игрока (`main.js`: `S.lean * 0.22`), чтобы
// собственный наклон и наклон противника выглядели одним движением.
// На хитбоксы это НЕ влияет: на сервере стопка коробок остаётся осевой, поэтому
// угол намеренно мал — при полном наклоне верх торса разъезжается с коробкой
// примерно на 5 см, вдвое меньше, чем щедрость самого хитбокса. Крутить выше
// нельзя: рисунок начнёт расходиться с попаданиями.
const LEAN_ROLL = 0.22;

// Скорость, на которой размах достигает полного. Обычная ходьба — 6 м/с,
// спринт 9: на бегу размах уже не растёт, зато частота шага растёт сама
// (фаза считается из пути).
const FULL_SWING_SPEED = 6.5;

/** Куда смотрит сегмент, повёрнутый на угол A: направление `(−sin A, −cos A)`
 *  в плоскости (z, y). Обратная к нему — это и есть «какой угол нужен, чтобы
 *  сегмент указал в (dz, dy)». */
const aimAngle = (dz, dy) => Math.atan2(-dz, -dy);

/** Двухзвенная обратная кинематика ноги в плоскости тела.
 *
 *  Дано: таз в (0, hipY), ступня в (footZ, footY). Найти углы бедра и
 *  колена, чтобы цепочка достала до ступни, а колено ушло ВПЕРЁД — колено
 *  назад человек не гнёт, и знак здесь не «один из двух вариантов», а
 *  единственный анатомически возможный.
 *
 *  `footY` по умолчанию `ANKLE_Y` (подошва на полу). Параметром он стал ради
 *  утиного шага: на выносе ступня отрывается от земли, и без подъёма она
 *  «шоркала» бы носком по полу вместо шага.
 *
 *  Возвращает [бедро, колено, ступня]. Ступня доворачивается так, чтобы её
 *  накопленный угол был нулевым, то есть подошва оставалась плашмя — ровно
 *  как у стоящего игрока, где все углы нулевые.
 *
 *  Расстояние зажимается в достижимое: без этого при высоком тазе (начало
 *  приседа) `acos` получил бы аргумент вне −1…1 и вернул NaN, а NaN в
 *  повороте гасит весь меш целиком. */
const _leg = [0, 0, 0];
function solveLeg(rig, hipY, footZ, footY) {
  const [L1, L2] = rig.legSegs;
  const dz = footZ, dy = footY - hipY;
  const reach = Math.min(Math.max(Math.hypot(dz, dy), Math.abs(L1 - L2) + 1e-3), L1 + L2 - 1e-3);
  const a = aimAngle(dz, dy);
  const alpha = Math.acos(Math.min(1, Math.max(-1,
    (L1 * L1 + reach * reach - L2 * L2) / (2 * L1 * reach))));
  const hip = a + alpha;                                  // «+» уводит колено вперёд
  const kz = -Math.sin(hip) * L1, ky = hipY - Math.cos(hip) * L1;
  const shin = aimAngle(footZ - kz, footY - ky);
  _leg[0] = hip; _leg[1] = shin - hip; _leg[2] = -shin;
  return _leg;
}

/** Складывает ноги в позу приседа/лёжа поверх уже посчитанной ходьбы.
 *
 *  ⚠️ Присед перестал быть «втягиванием» именно здесь: длина ноги постоянна
 *  (см. LEG_SEGS), а вниз игрока опускает СГИБ. Ступни при этом разнесены
 *  вперёд-назад — симметрично сложенные ноги читаются как «сел на корточки»,
 *  а выставленная вперёд опорная — как присед в бою.
 *
 *  ⚠️ Поза кладётся весом поверх ходьбы, а не вместо неё: присев, ходить всё
 *  ещё можно, и переключение дало бы щелчок на каждом приседании.
 *
 *  ⚠️ …но на ПОЛНОЙ позе вес равен 1, то есть качание из `animateLimbs`
 *  перезаписывается целиком — и присевший с лежащим ехали с каменными ногами
 *  (жалоба владельца). Поэтому движение здесь заведено не поверх позы,
 *  а ВНУТРИ неё: у приседа едут цели ступней (`KNEEL_STRIDE`), у лежащего —
 *  углы гребка (`CRAWL_*`). Фаза у обоих та же, что у ходьбы, то есть
 *  считается из пройденного пути и в такт звуку шагов. */
function poseLegs(r, kneel, prone) {
  const { legL, legR } = r.limbs;
  const rig = r.rig || BOX_RIG;
  const sw = Math.sin(r.phase || 0), cw = Math.cos(r.phase || 0);

  const put = (leg, angles, w) => {
    for (let i = 0; i < 3; i++) {
      leg.joints[i].rotation.x = mix(leg.joints[i].rotation.x, angles[i], w);
    }
  };

  if (kneel > 0.001) {
    // Высоту таза берём ФАКТИЧЕСКУЮ (её только что поставил `layoutBody`),
    // а не «ту, что будет внизу»: на переходе нога обязана доставать до пола
    // из текущего положения, иначе ступни повиснут в воздухе.
    const hipY = r.limbs.legL.root.position.y;
    // Утиный шаг: чем быстрее едет игрок, тем сильнее ступни расходятся
    // из боевой стойки в шаг. Стоя на месте (`walk` = 0) поза прежняя.
    const walk = Math.min(1, (r.speed || 0) / KNEEL_FULL_SPEED);
    const baseL = mix(FRONT_FOOT_Z, KNEEL_MID_Z, walk);
    const baseR = mix(BACK_FOOT_Z, KNEEL_MID_Z, walk);
    const stride = KNEEL_STRIDE * walk, lift = KNEEL_LIFT * walk;
    // ⚠️ Ступня поднимается на ВЫНОСЕ, а не когда она впереди: вперёд она
    // едет там, где производная её пути отрицательна, то есть по косинусу
    // фазы. Подними по синусу — и нога будет висеть в воздухе в опорной фазе
    // и шоркать носком в переносной, то есть ровно наоборот.
    put(legL, solveLeg(rig, hipY, baseL - sw * stride, rig.ankleY + Math.max(0, cw) * lift), kneel);
    put(legR, solveLeg(rig, hipY, baseR + sw * stride, rig.ankleY + Math.max(0, -cw) * lift), kneel);
  }
  if (prone > 0.001) {
    // Лёжа цели ступней не нужны: нога вытянута назад вдоль тела, а ползание
    // набирается углами (см. CRAWL_*). Гребут ноги по очереди — левая на
    // положительной полуволне фазы, правая на отрицательной.
    const crawl = Math.min(1, (r.speed || 0) / CRAWL_FULL_SPEED);
    const strokeL = Math.max(0, sw) * crawl, strokeR = Math.max(0, -sw) * crawl;
    // ⚠️ Ступню доворачиваем обратно (`-PRONE_HIP_A` плюс угол колена) — по
    // тому же правилу, что в `solveLeg`: накопленный угол ступни всегда
    // нулевой, иначе она встаёт на ребро и своей длиной (38 см) уходит под пол.
    const stroke = (w) => {
      _leg[0] = PRONE_HIP_A;
      _leg[1] = -CRAWL_KNEE_UP * w;
      _leg[2] = -PRONE_HIP_A + CRAWL_KNEE_UP * w;
      return _leg;
    };
    put(legL, stroke(strokeL), prone);
    put(legR, stroke(strokeR), prone);
    // Разведение: базовое (иначе две параллельные коробки читаются как хвост)
    // плюс отвод бедра на гребке. Голень уходит вбок за коленом — поворотом
    // вокруг Z, который лёжа работает как «в сторону», а не «вверх».
    legL.joints[0].rotation.z = -(PRONE_SPREAD + CRAWL_HIP_OUT * strokeL) * prone;
    legR.joints[0].rotation.z = (PRONE_SPREAD + CRAWL_HIP_OUT * strokeR) * prone;
    legL.joints[1].rotation.z = -CRAWL_KNEE_OUT * strokeL * prone;
    legR.joints[1].rotation.z = CRAWL_KNEE_OUT * strokeR * prone;
  } else {
    // ⚠️ Гасить надо ОБА шарнира: колено по Z крутит только ползание, и
    // оставленный с прошлого кадра угол пережил бы вставание — игрок пошёл бы
    // с вывернутой вбок голенью.
    legL.joints[0].rotation.z = legR.joints[0].rotation.z = 0;
    legL.joints[1].rotation.z = legR.joints[1].rotation.z = 0;
  }

  // ---- Ступня не уходит под пол ----
  //
  // ⚠️ Это лечение СТАРОЙ дыры, а не следствие человеческой модели. Позы
  // смешиваются УГЛАМИ, а середина между «нога прямая» и «нога сложена» —
  // это нога, воткнутая в землю. Держится оно доли секунды (переход ~0.15 с)
  // и потому долго не замечалось; замер `#check` в стенде показал до 0.79 м
  // под полом на полпути к позе «лёжа», где таз уже почти на земле, а нога
  // ещё выпрямляется.
  //
  // Лечим самым дешёвым способом из возможных: смотрим, где ФАКТИЧЕСКИ
  // оказалась лодыжка, и если она ниже пола — пересчитываем ногу ИК на ту же
  // точку по Z, но с лодыжкой на своей высоте. Смешивать вместо углов ЦЕЛИ
  // было бы правильнее, но это переписывание всей ходьбы, а она держится на
  // углах намеренно (колено гнётся только назад и только на возвратном ходе).
  //
  // ⚠️ Положение берём из САМОЙ цепочки (`matrixWorld`), а не считаем плоской
  // формулой по углам: ползание разводит ногу вбок (`rotation.z`), и плоская
  // формула этого не видит — первая версия клампа оставляла до 0.39 м под
  // полом именно там.
  const hipY = legL.root.position.y;
  for (const leg of [legL, legR]) {
    const ankle = leg.joints[2];
    ankle.updateWorldMatrix(true, false);
    _foot.setFromMatrixPosition(ankle.matrixWorld);
    leg.root.parent.worldToLocal(_foot);              // → система тела
    if (_foot.y >= rig.ankleY - 1e-3) continue;
    const fix = solveLeg(rig, hipY, _foot.z, rig.ankleY);
    for (let i = 0; i < 3; i++) leg.joints[i].rotation.x = fix[i];
  }
}

/** Поворот шарниров по фазе шага. Положительный `rotation.x` уводит сегмент
 *  вперёд (−Z): туда же смотрит нос-указатель.
 *
 *  Потребителей два: чужие игроки (ниже по файлу) и СВОЁ тело от первого лица
 *  (`body.js`). Второму нужны ровно те же ноги — свои и чужие обязаны шагать
 *  одинаково, иначе походка на экране и походка в чужих глазах разъедутся. */
export function animateLimbs(r, pitch) {
  const kneel = r.kneel || 0, prone = r.prone || 0;
  // Присев, шагать всё ещё можно (утиный шаг), поэтому размах не гасим совсем;
  // лёжа — почти гасим: ползущий не машет ногами.
  const amp = Math.min(1, r.speed / FULL_SWING_SPEED) * (1 - kneel * 0.6) * (1 - prone * 0.8);
  const sw = Math.sin(r.phase) * amp;
  const { legL, legR, armL, armR } = r.limbs;

  // Ноги в противофазе друг другу, руки — в противофазе ногам.
  const hipL = sw * 0.62, hipR = -sw * 0.62;
  // Колено гнётся только назад и только на возвратном ходе ноги: вперёд оно
  // не гнётся вовсе, и «прямая» нога на выносе — как раз то, что делает
  // походку походкой, а не качанием маятников.
  const kneeL = -Math.max(0, -sw) * 0.9, kneeR = -Math.max(0, sw) * 0.9;
  legL.joints[0].rotation.x = hipL;
  legR.joints[0].rotation.x = hipR;
  legL.joints[1].rotation.x = kneeL;
  legR.joints[1].rotation.x = kneeR;
  // Ступня частично отыгрывает поворот ноги назад — иначе носок «клюёт» пол.
  legL.joints[2].rotation.x = -(hipL + kneeL) * 0.6;
  legR.joints[2].rotation.x = -(hipR + kneeR) * 0.6;

  // Поза кладётся ПОВЕРХ ходьбы весом, а не вместо неё: присевший игрок
  // продолжает переступать, и переключение поз дало бы щелчок на каждом
  // приседании. Тот же приём, что у поз рук (см. POSE_*).
  poseLegs(r, kneel, prone);

  // Свободная рука: качание в противофазе ногам плюс питч. Именно СВОБОДНАЯ —
  // всё, что держит оружие, ниже переписывается хватом целиком. У пистолета
  // и гранаты свободна левая (держим одной рукой), у винтовки и снайперки —
  // ни одной.
  armL.joints[0].rotation.set(ARM_BASE + pitch - sw * 0.45, 0, 0);
  armR.joints[0].rotation.set(ARM_BASE + pitch + sw * 0.45, 0, 0);
  armL.joints[1].rotation.set(ELBOW_BASE, 0, 0);
  armR.joints[1].rotation.set(ELBOW_BASE, 0, 0);
  // Кисть сбрасываем: её крутит только хват, и оставленный с прошлого кадра
  // поворот пережил бы смену оружия (винтовка → пистолет освобождает руку).
  armL.joints[2].rotation.set(0, 0, 0);
  armR.joints[2].rotation.set(0, 0, 0);

  // Голова кивает лишь частично — см. HEAD_PITCH_SHARE.
  r.parts.head.rotation.x = pitch * HEAD_PITCH_SHARE;

  // Лёжа свободная рука встаёт на локоть под грудь, а предплечье идёт вперёд —
  // человек лежит НА локтях, а не роняет руки вдоль тела.
  if (prone > 0.001) {
    for (const arm of [armL, armR]) {
      arm.joints[0].rotation.x = mix(arm.joints[0].rotation.x, PRONE_ARM, prone);
      arm.joints[1].rotation.x = mix(arm.joints[1].rotation.x, PRONE_ELBOW, prone);
      arm.joints[0].rotation.z = mix(arm.joints[0].rotation.z, 0, prone);
    }
  }

  // ---- Хват: кисти доводятся до точек, посчитанных `applyHands` ----
  //
  // Идёт ПОСЛЕДНИМ и поверх всего: качание, прицел и поза лёжа описывают, что
  // делает свободная рука, а хват — где физически находится кисть, и спорить
  // им не о чем. Правая держит всегда (в руке у игрока всегда что-то есть),
  // левая — по весу `two`: только у оружия под две руки.
  const h = r.hands;
  const lens = r.armLens || ARM_SEGS;
  // Ось «вбок у оружия» — по ней разворачиваются ладони (см. `reachArm`).
  // Считается здесь, а не в `applyHands`: у трупа и у своего тела кадр рук
  // может прийти без неё, а поворот пивота живой всегда.
  _gunRight.set(1, 0, 0).applyEuler(h.pivot.rotation);
  reachArm(armR, lens, +1, h.gripR, h.ads, 1, _gunRight);
  reachArm(armL, lens, -1, h.gripL, h.ads, h.two, _gunRight);

  // ⚠️ ПОСЛЕДНИМ в кадре и только здесь: модель повторяет костями уже готовую
  // позу рига. Позови раньше — она отстанет на кадр (видно как «плывёт» на
  // быстром повороте), позови в двух местах — заплатишь дважды.
  if (r.skin) syncSkin(r);
}

/** Что у чужого в руках: выбор предмета, веса поз, положение и поворот.
 *
 *  `held` — байт из снапшота (раскладка в `net.js`). Единственный источник:
 *  выводить оружие из чего-то ещё клиент не может и не должен.
 *
 *  ⚠️ Исключение ровно одно и оно не про чужих: своё тело (`body.js`) собирает
 *  этот байт из локального состояния — снапшот САМОГО СЕБЯ клиент не получает
 *  (сервер вырезает получателя, см. `updateRemotes`). Спорить там не с чем:
 *  это своё же намерение, и видно оно только в собственной тени. */
export function applyHands(r, held, eye, lean, pitch, dt) {
  const h = r.hands;
  const isNade = (held & HELD_NADE) !== 0;

  // Видимой оставляем ровно одну вещь (см. `showHeld` — она же нужна трупу).
  showHeld(h, held);

  // Веса поз ползут к цели, а не прыгают: длительности анимаций клиент не
  // знает (и знать не должен, см. комментарий у POSE_*), а плавность нужна.
  // Прицеливание с гранатой в руке невозможно — сервер такого не пришлёт,
  // но на всякий случай гасим здесь же: поза «целюсь гранатой» выглядела бы
  // поломкой, а не читерством.
  h.ads = ease(h.ads, !isNade && (held & HELD_ADS) ? 1 : 0, ADS_SPEED, dt);

  // ---- Чужую перезарядку оборвали бегом — глушим её звук ----
  //
  // Бит HELD_RELOAD гаснет и по нормальному окончанию, и по обрыву; отличает
  // их взведённый в тот же кадр HELD_SPRINT (сервер отменяет перезарядку
  // ровно тогда, когда игрок побежал, — `room::apply_input`). Гасить нужно
  // потому, что семпл длится почти всю перезарядку: доиграв, он сказал бы
  // «противник вот-вот снова опасен» про того, кто просто убежал.
  //
  // ⚠️ Ищем звук ПО ТОЧКЕ, а не по id: в кадре OP_SOUND источника нет
  // намеренно (античит, см. shared/src/sound.rs). Точка — позиция глаз, ровно
  // та, из которой сервер породил звук в `room::reload`.
  const nowReload = !isNade && (held & HELD_RELOAD) !== 0;
  if (h.wasReload && !nowReload && (held & HELD_SPRINT)) {
    stopReloadNear(r.group.position.x, r.group.position.y + eye, r.group.position.z);
  }
  h.wasReload = nowReload;

  h.reload = ease(h.reload, nowReload ? 1 : 0, POSE_SPEED, dt);
  h.draw = ease(h.draw, !isNade && (held & HELD_DRAW) ? 1 : 0, POSE_SPEED, dt);
  h.nade = ease(h.nade, isNade ? 1 : 0, POSE_SPEED, dt);
  // Бег гасится быстрее, чем набирается (ADS_SPEED против POSE_SPEED): оружие
  // вскидывают резче, чем опускают, и именно момент вскидывания противнику
  // важно увидеть вовремя — по нему он понимает, что тот снова может стрелять.
  h.sprint = ease(h.sprint, (held & HELD_SPRINT) ? 1 : 0,
    (held & HELD_SPRINT) ? POSE_SPEED : ADS_SPEED, dt);

  // Смешиваем от бедра к остальному. Порядок задаёт приоритет: доставание
  // перекрывает и прицел, и перезарядку — оружие в этот момент физически
  // ещё не в позиции, что бы игрок ни зажимал.
  for (let i = 0; i < POSE_LEN; i++) _pose[i] = POSE_HIP[i];
  mixPose(_pose, POSE_ADS, h.ads);
  // Бег перекрывает прицел (сервер их и не пришлёт вместе), но уступает
  // перезарядке и доставанию: возятся с оружием руками и на бегу тоже.
  mixPose(_pose, POSE_SPRINT, h.sprint);
  mixPose(_pose, POSE_NADE, h.nade);
  mixPose(_pose, POSE_RELOAD, h.reload);
  mixPose(_pose, POSE_DRAW, h.draw);
  // Поза тела перекрывает всё: лёжа оружие физически не поднять к плечу,
  // что бы игрок ни зажимал. Вес берётся из раскладки тела (`layoutBody`),
  // то есть из той же высоты глаз, по которой сервер считает хитбоксы.
  mixPose(_pose, POSE_PRONE, r.prone || 0);

  // Наклон Q/E утаскивает оружие вместе с верхом тела — на той же высоте
  // и с тем же весом, что и плечи (иначе ствол оставался бы за укрытием,
  // пока хозяин уже выглянул).
  // ⚠️ Позы `POSE_*` подбирались под КОРОБОЧНЫЙ риг, у которого плечо на 8.5 см
  // выше, а рука на 8% длиннее. Отдай их человеческой модели как есть — точка
  // хвата окажется за пределом её руки, ИК зажмёт цель по длине, и персонаж
  // будет держать оружие на вытянутых в струну руках (видно сразу, проверено
  // рендером). Поэтому оружие едет вслед за плечом: вниз на разницу высот и
  // ближе к телу на отношение длин рук. Переписывать все шесть поз под вторую
  // модель нельзя — это завело бы второй набор баланса позы.
  const rig = r.rig || BOX_RIG;
  const y = eye + _pose[1] - rig.handDrop;
  h.pivot.position.set(_pose[0] * rig.handReach + lean * leanWeight(y, eye),
    y, _pose[2] * rig.handReach);
  // По X ствол ведём питчем: смотрящий вверх поднимает и оружие. Доля берётся
  // из позы (_pose[3]) — от бедра ствол задирается заметно слабее взгляда,
  // в прицеле идёт с ним точно.
  // По Z ствол кренится вместе с корпусом (см. LEAN_ROLL): выглянувший
  // из-за угла держит оружие завалившимся, а не строго горизонтально.
  // ⚠️ К доле питча добавляется ПОСТОЯННЫЙ наклон позы (`_pose[6]`): опущенное
  // оружие смотрит в землю даже у того, кто глядит прямо.
  h.pivot.rotation.set(pitch * _pose[3] + _pose[6], _pose[4], _pose[5] - (lean / LEAN_OFFSET) * LEAN_ROLL);

  // Перезарядка ещё и покачивает ствол: неподвижное оружие в опущенной руке
  // читается как «завис», а не как «возится с магазином». Дёшево и работает
  // при любой длительности — ровно то, что нужно (см. POSE_*).
  if (h.reload > 0.01) {
    h.pivot.rotation.z += Math.sin(clock.elapsedTime * 9) * 0.25 * h.reload;
  }

  // Лёгкое качание оружия при ходьбе. Раньше жизнь силуэту давал мах рук,
  // но руки теперь приклеены к стволу (см. `reachArm`) и качаться не могут —
  // значит качаться должно то, что они держат, иначе идущий с винтовкой
  // выглядит выключенным. Амплитуда намеренно крошечная: оружие ведёт корпус,
  // а не мотает из стороны в сторону.
  const bob = Math.min(1, (r.speed || 0) / FULL_SWING_SPEED) * (1 - (r.prone || 0));
  if (bob > 0.01) {
    h.pivot.position.y += Math.sin(r.phase * 2) * 0.012 * bob;
    h.pivot.rotation.z += Math.sin(r.phase) * 0.05 * bob;
  }

  // ---- Куда лягут кисти ----
  //
  // Точки хвата живут в системе МОДЕЛИ предмета, а рукам нужна система тела.
  // Пивот — прямой ребёнок группы, поэтому перевод стоит поворот плюс сдвиг;
  // матрицы мира для этого не нужны и обновлять их (лишний проход по сцене)
  // не приходится.
  const item = held & HELD_ITEM_MASK;
  const gr = isNade ? GRIP_NADE : GRIP_R;
  h.gripR.set(gr[0], gr[1], gr[2]).applyEuler(h.pivot.rotation).add(h.pivot.position);

  // Вторая рука: цевьё, а на перезарядке — магазин под ствольной коробкой.
  //
  // Вес `two` ползёт, а не прыгает: смена винтовки на пистолет иначе роняла бы
  // левую руку рывком. На доставании он гасится совсем — оружие поднимают
  // одной рукой, вторая приходит на цевьё уже потом. Это не только правдиво,
  // но и снимает единственную позу, где до цевья было не дотянуться (расчёт
  // в CLAUDE.md 2.3.5): опущенный вниз ствол уносит цевьё за длину руки.
  const gl = isNade ? null : GRIP_L[item % 3];
  h.two = ease(h.two, gl ? 1 - h.draw : 0, POSE_SPEED, dt);
  if (gl) {
    const t = h.reload;
    h.gripL
      .set(mix(gl[0], GRIP_MAG[0], t), mix(gl[1], GRIP_MAG[1], t), mix(gl[2], GRIP_MAG[2], t))
      .applyEuler(h.pivot.rotation).add(h.pivot.position);
  }
}

function removeRemote(id) {
  const r = remotes.get(id);
  if (!r) return;
  scene.remove(r.group);
  // UNIT_BOX и материалы тела общие — их не трогаем, иначе развалятся
  // остальные игроки. То же и с оружием и гранатами: они клонированы из общих
  // GLB-шаблонов (worldTpl/nademodel), геометрия и материалы там общие —
  // освобождать нечего.
  // Своё у меша только конус носа и текстура ника.
  r.nose.geometry.dispose();
  r.nose.material.dispose();
  // Скелет модели: геометрия и материалы общие с шаблоном (клон через
  // SkeletonUtils), освобождать нечего — но снять со сцены и вычеркнуть из
  // очереди ожидания надо, иначе догрузившийся GLB воскресит удалённое тело.
  dropSkin(r.skin);
  r.label.material.map.dispose();
  r.label.material.dispose();
  if (r.hpBar) {
    r.hpBar.tex.dispose();
    r.hpBar.sprite.material.dispose();
  }
  remotes.delete(id);
  // ⚠️ Ник здесь НЕ удаляем: эта функция зовётся и по FORGET_MS (меш выгружен,
  // но игрок остаётся в комнате, просто скрыт PVS). Забываем ник только по
  // настоящему LEAVE — иначе вернувшийся игрок теряет имя и становится «#id».
}

/**
 * Наша ли это команда — по биту `F_CT` из снапшота.
 *
 * ⚠️ Своя команда живёт в `lobby.js` (кадр `OP_MATCH`), но импортировать его
 * сюда нельзя: `lobby.js` тянет `remote.js` (ему нужен `dropRemotes`), и вышел
 * бы цикл модулей. Поэтому свою команду сюда КЛАДЁТ `lobby.js` вызовом
 * `setMyTeam` — тот же приём, что у `onRemoteDeath` и `LEAN_OFFSET`
 * в `skinbody.js`.
 *
 * В тренировке команд нет: `myTeam` там `TEAM_NONE`, и союзников не бывает —
 * ровно как было до матчмейкинга.
 */
let myTeam = 255;
export function setMyTeam(team) { myTeam = team; }
function isAlly(flags) {
  if (myTeam !== 0 && myTeam !== 1) return false;
  const theirs = (flags & F_CT) !== 0 ? 1 : 0;
  return theirs === myTeam;
}

/**
 * Убрать со сцены ВСЕХ чужих и забыть их ники и снапшоты.
 *
 * Зовёт `lobby.js` при выходе из матча. Без этого тела остались бы висеть
 * декорацией: кадры `LEAVE` про них нам уже не придут — из комнаты вышли МЫ,
 * и сервер рассылает их тем, кто остался.
 */
export function dropRemotes() {
  for (const id of [...remotes.keys()]) removeRemote(id);
  nicks.clear();
  snapshots.length = 0;
  lastPresent.clear();
}

// ---- Отрисовка ----

/** Кратчайшая интерполяция угла: через 0/2π, а не «в обход». */
function lerpAngle(a, b, t) {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/** Время прошлого кадра отрисовки — нужно только для оценки скорости чужих. */
let lastFrame = 0;

export function updateRemotes() {
  if (snapshots.length < 2) return;
  const now = performance.now();
  const target = now - interpDelay;
  // Ограничение сверху: после вкладки в фоне между кадрами проходят секунды,
  // и без клампа скорость чужого улетела бы в мусор на первом же кадре.
  const frameDt = lastFrame ? Math.min(0.1, (now - lastFrame) / 1000) : 0;
  lastFrame = now;

  // Ищем пару снапшотов вокруг целевого времени
  let a = null, b = null;
  for (let i = snapshots.length - 1; i > 0; i--) {
    if (snapshots[i - 1].t <= target && snapshots[i].t >= target) {
      a = snapshots[i - 1]; b = snapshots[i];
      break;
    }
  }

  // Пары нет — но причины у этого ДВЕ, и лечатся они противоположно.
  //
  // 1) target СТАРШЕ самого свежего снапшота — буфер опустел (джиттер съел
  //    пакет). Данные есть, они просто кончились. Прятать здесь нельзя:
  //    в снапшоте все, кого видно, поэтому скрывались бы ВСЕ разом на
  //    долю секунды — это и есть баг «боты моргают одновременно».
  //    Правильно — доехать по последней паре экстраполяцией (t > 1),
  //    ограниченной EXTRAPOLATE_MS, дальше замереть на последней позиции.
  //
  // 2) ⚠️ БАГ-УРОК: target МОЛОЖЕ самого старого снапшота — истории ещё нет
  //    (игрок только что появился из-за угла). Раньше здесь стоял фолбэк
  //    «берём самый свежий»: сначала рисуем его в АКТУАЛЬНОЙ позиции, а когда
  //    буфер наполнится — переключаемся на отрисовку с отставанием interpDelay,
  //    и он отпрыгивает НАЗАД на delay × скорость (≈2 м на спринте). Выглядело
  //    как телепорт. Здесь по-прежнему НЕ рисуем: игрок появится на interpDelay
  //    позже, но поедет плавно и в верную сторону.
  if (!a) {
    const last = snapshots[snapshots.length - 1];
    if (target < snapshots[0].t) {          // случай 2 — истории нет
      for (const r of remotes.values()) { r.group.visible = false; r.tracked = false; }
      return;
    }
    // случай 1 — буфер опустел
    a = snapshots[snapshots.length - 2];
    b = last;
    // Буфер оказался мал для реального джиттера: σ его не предсказала.
    // Растим задержку по ФАКТУ нехватки — надёжнее любой статистики; вниз её
    // потом медленно вернёт trackJitter. ⚠️ Ровно ОДИН раз за эпизод, а не
    // каждый кадр: кадров тут 60-144 в секунду, и «+ на кадр» за одну
    // просадку выбило бы задержку в потолок DELAY_MAX.
    if (starveMark !== lastRecv) {
      starveMark = lastRecv;
      interpDelay = Math.min(DELAY_MAX, interpDelay + TICK_MS);
      S.interpDelay = interpDelay;
    }
  }

  const span = b.t - a.t;
  let t = span > 0 ? (target - a.t) / span : 0;
  // Экстраполяция ограничена: за её пределами честнее замереть, чем уехать.
  // Свернувший игрок при 100 мс промахнётся максимум на ~0.9 м, и ошибка
  // сама рассосётся следующим снапшотом — а вот исчезновение цели с экрана
  // не «рассасывается», игрок успевает его заметить.
  if (span > 0) t = Math.min(t, (b.t + EXTRAPOLATE_MS - a.t) / span);

  const seen = new Set();
  for (const [id, sa] of a.players) {
    if (id === me.id) continue;          // себя рисует основной игровой цикл
    seen.add(id);
    const sb = b.players.get(id) || sa;
    // Меш строим только под ЖИВОГО. Мёртвая запись у того, кого мы живым
    // ни разу не нарисовали (вошли в игру ровно в эти 400 мс), ронять всё
    // равно нечего, а меш стоит канваса с ником и полоски HP.
    let r = remotes.get(id);
    if (!r) {
      if ((sa.flags & F_ALIVE) === 0) continue;
      // ⚠️ Команда читается ЗДЕСЬ, при создании тела: цвет союзника ставится
      // набором материалов один раз, а не перекрашивается каждый кадр. Смена
      // команды посреди матча невозможна (слот занимается до боя), поэтому
      // пересобирать тело не придётся.
      r = createRemote(id, sa.npc, isAlly(sa.flags));
    }

    // ⚠️ ГЛАВНОЕ МЕСТО СМЕРТИ (протокол 16). Убитый едет в снапшоте ещё
    // DEATH_LINGER (400 мс) с погашенным F_ALIVE — то есть «он умер» приходит
    // ПОТОКОМ снапшотов, а не одним кадром OP_KILL. Раньше признак был ровно
    // один — тот самый кадр, и на канале WebRTC (ordered:false,
    // maxRetransmits:0) его потеря означала, что клиент считал смерть уходом
    // за укрытие и просто прятал игрока: жалоба владельца «убитые боты иногда
    // исчезают без тела».
    //
    // ⚠️ Флаг берём из `sa`, а не из `sb`: тело роняется там и тогда, где
    // интерполяция его РИСУЕТ. Кадр `sb` — это будущее на треть тика вперёд,
    // и по нему смерть срабатывала бы раньше картинки.
    const alive = (sa.flags & F_ALIVE) !== 0;
    r.group.visible = alive;
    if (!alive) {
      r.tracked = false;
      if (r.wasAlive) {
        r.wasAlive = false;
        // Позиция берётся из мёртвой записи: сервер не двигает убитого,
        // значит это авторитетная точка смерти, а не «где мы его в прошлый
        // раз нарисовали». Высота в кадре — уровень ГЛАЗ, телу нужны ступни.
        deathHook?.(id, {
          x: sa.x, y: sa.y - sa.eye, z: sa.z,
          eye: sa.eye, yaw: sa.yaw, lean: sa.lean,
          held: sa.held, npc: r.npc,
        }, true);
      }
      continue;
    }
    r.wasAlive = true;

    // Высота в снапшоте — уровень ГЛАЗ. Группу ставим СТУПНЯМИ на пол:
    // так раскладка тела совпадает с серверной (`hit.rs` тоже считает от ног).
    const eyeY = sa.y + (sb.y - sa.y) * t;
    // ⚠️ Поза берётся из ФАКТИЧЕСКОЙ высоты глаз, а не из флагов
    // F_CROUCH/F_PRONE. Флаг — это зажатая кнопка, он переключается мгновенно,
    // а тело опускается за ~0.15 с: пока клиент выводил позу из флага, всё
    // время перехода он рисовал уже присевшего там, где сервер считал коробки
    // ещё для стоящего. Высота — обычная величина, интерполируется как
    // позиция.
    const eye = sa.eye + ((sb.eye ?? sa.eye) - sa.eye) * t;
    r.group.position.set(
      sa.x + (sb.x - sa.x) * t,
      eyeY - eye,
      sa.z + (sb.z - sa.z) * t);
    r.group.rotation.y = lerpAngle(sa.yaw, sb.yaw, t);
    // Наклон — обычная величина, а не угол: через 0/2π не ходит,
    // кратчайшая дуга ему не нужна.
    const lean = sa.lean + ((sb.lean ?? sa.lean) - sa.lean) * t;
    // Питч — обычная величина в пределах ±MAX_PITCH, через 0/2π не ходит:
    // кратчайшая дуга ему не нужна, как и наклону.
    // ⚠️ Считается ДО `layoutBody`: та кладёт питч на тело для `syncSkin`
    // (кивок шеи и головы у человеческой модели). Считай ниже — модель
    // кивала бы с отставанием на кадр.
    const pitch = sa.pitch + ((sb.pitch ?? sa.pitch) - sa.pitch) * t;
    // Присед/лёжа складывают ноги, а не сжимают человека целиком: голова и
    // торс сохраняют размер. Это же правило у серверных хитбоксов.
    layoutBody(r, eye, lean, pitch);

    // Фаза шага — из пройденного пути (см. STEP_DIST). `tracked` отличает
    // «стоял на месте» от «появился заново после укрытия»: во втором случае
    // разница позиций огромна, и без флага она дала бы скачок фазы и всплеск
    // скорости — чужой на миг задёргался бы спринтом на выходе из-за угла.
    const px = r.group.position.x, pz = r.group.position.z;
    if (r.tracked && frameDt > 0) {
      const dx = px - r.prevX, dz = pz - r.prevZ;
      const d = Math.hypot(dx, dz);
      // ⚠️ Направление берётся из ТЕХ ЖЕ разниц позиций, что и скорость, —
      // протокол для этого не нужен: курс `yaw` в снапшоте уже есть, а куда
      // человек едет, видно по его собственному следу.
      stepPhase(r, d, forwardSpeed(dx / frameDt, dz / frameDt, r.group.rotation.y));
      // Скорость сглаживаем: она считается из интерполированных позиций, и на
      // джиттере ±60 мс сырое значение прыгало бы вместе с буфером.
      r.speed += (d / frameDt - r.speed) * 0.2;
    }
    // Часы покоя: по ним идут дыхание и перенос веса (см. `syncSkin`).
    // ⚠️ Копятся у КАЖДОГО свои и стартуют со случайного места — иначе
    // шестеро ботов дышали бы одной грудью.
    r.idle += frameDt;
    r.prevX = px; r.prevZ = pz; r.tracked = true;
    // Оружие ставим ДО рук: `animateLimbs` доводит руки к уже посчитанным
    // весам поз (`r.hands.ads` и прочим), а не наоборот.
    // ⚠️ Байт «в руках» НЕ интерполируем — это набор флагов, а не величина:
    // среднее между «перезаряжается» и «нет» смысла не имеет. Берём кадр `sb`,
    // то есть более свежий из пары: состояние должно опаздывать минимально.
    const held = sb.held ?? sa.held ?? 0;
    applyHands(r, held, eye, lean, pitch, frameDt);
    animateLimbs(r, pitch);

    // Последнее ЖИВОЕ состояние — по нему собирается труп: он обязан
    // появиться в той же позе и с тем же предметом в руках, иначе смерть
    // читается как подмена одной модели другой.
    r.lastEye = eye; r.lastLean = lean; r.lastHeld = held;

    // Всё, что рисуется с depthTest:false, обязано само проверять стены —
    // иначе светит сквозь укрытие (см. `occluded`). Один рейкаст на цель.
    // Горящий человек (бит F_BURNING, протокол 18) — пламя по всему телу.
    // Рисуем только видимого: сквозь стену огонь светил бы наводкой на цель,
    // которую PVS как раз и прячет.
    const hidden = occluded(r.group.position.x, eyeY, r.group.position.z);
    if ((sa.flags & F_BURNING) !== 0 && !hidden && frameDt > 0) {
      spawnBodyFire(r.group.position.x, r.group.position.y, r.group.position.z, eye, frameDt);
    }
    r.label.visible = !hidden;
    if (r.hpBar) {
      drawHpBar(r.hpBar, sb.hp);
      r.hpBar.sprite.visible = !hidden;
    }
  }

  // ⚠️ Игрока, которого нет в ТЕКУЩЕМ интерполируемом кадре, НЕЛЬЗЯ удалять:
  // его может не быть просто потому, что интерполяция ещё не дошла до кадра,
  // где он появился (PVS отдал его только что). Удаление здесь приводило бы
  // к пересозданию меша на каждом выходе из-за угла — с потерей истории и
  // рывком. Поэтому: скрываем, а удаляем только по-настоящему пропавших.
  for (const [id, r] of remotes) {
    if (seen.has(id)) continue;
    // ⚠️ ВТОРОЙ, СТРАХОВОЧНЫЙ путь рождения трупа (основной — снятый F_ALIVE
    // выше). Игрок пропал из кадра, и причин ровно две: ушёл за укрытие (PVS)
    // или умер там, где мёртвая запись до нас уже не доехала (VIS_GRACE
    // истекла раньше DEATH_LINGER, кадры потерялись целой пачкой). Отличить
    // их самому клиенту нечем, и гадать не нужно: если приходил кадр OP_KILL
    // на этот id, значит смерть (решает `corpse.js`, проверяя свою очередь
    // `pending`; нет записи — просто спрятался).
    //
    // Двойного тела эти два пути не дают: родив труп по флагу, мы снимаем
    // `wasAlive`, и сюда игрок уже не попадёт.
    //
    // Почему тело роняется по ПРОПАЖЕ, а не сразу по кадру OP_KILL: кадр
    // приходит в реальном времени, а тело нарисовано на interpDelay
    // (150-400 мс) раньше. Родив труп по кадру, мы поставили бы его туда,
    // где игрок ещё только будет, — на спринте это больше метра. Пропажа же
    // приходит по той же временной оси, что и картинка.
    if (r.wasAlive) {
      r.wasAlive = false;
      deathHook?.(id, {
        x: r.group.position.x, y: r.group.position.y, z: r.group.position.z,
        eye: r.lastEye, yaw: r.group.rotation.y, lean: r.lastLean,
        held: r.lastHeld, npc: r.npc,
      });
    }
    r.group.visible = false;
    r.tracked = false;
  }
  for (const id of [...remotes.keys()]) {
    const last = lastPresent.get(id) || 0;
    if (now - last > FORGET_MS) removeRemote(id);
  }
}

/** Сколько живых чужих игроков сейчас в комнате (для HUD и радара). */
export function remoteList() {
  return remotes;
}

// ---- Куда на пути пули попали чужие тела ----
//
// Пуля прошивает людей (протокол 19): она не останавливается на первом, идёт
// дальше и кончается в стене. Урон по-прежнему считает СЕРВЕР — этот луч
// нужен исключительно затем, чтобы нарисовать брызги там, где пуля прошла
// сквозь человека, и его результат ни на что в игре не влияет.
//
// ⚠️ `raycaster.camera` обязателен: рекурсивный обход группы тела задевает
// спрайты (ник, полоска HP), а Three.js без камеры бросает исключение ВНУТРИ
// intersectObjects — до любого фильтра. Ровно этот баг однажды убил всю
// визуализацию выстрела (баг-урок в md/netcode.md).
const _bodyRay = new THREE.Raycaster();
_bodyRay.camera = camera;
const _bodyDir = new THREE.Vector3();

/**
 * Точки, где отрезок `from → to` вошёл в чужие тела.
 *
 * На одно тело — одна точка (первое касание): луч вдоль человека задевает
 * и торс, и таз, а брызг от этого больше становиться не должно.
 * Возвращает массив THREE.Vector3 в порядке от стрелка.
 */
export function bodiesAlong(from, to) {
  const bodies = [];
  for (const r of remotes.values()) if (r.group.visible) bodies.push(r.group);
  if (!bodies.length) return [];
  _bodyDir.subVectors(to, from);
  const len = _bodyDir.length();
  if (len < 1e-3) return [];
  _bodyRay.set(from, _bodyDir.multiplyScalar(1 / len));
  _bodyRay.far = len;
  const roots = new Set(bodies);
  const seen = new Set();
  const out = [];
  for (const h of _bodyRay.intersectObjects(bodies, true)) {
    if (h.object.isSprite) continue;     // стрелять «в надпись» нельзя
    let root = h.object;
    while (root && !roots.has(root)) root = root.parent;
    if (!root || seen.has(root)) continue;
    seen.add(root);
    out.push(h.point.clone());
  }
  return out;
}
